import { useEffect, useLayoutEffect, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { checkSharePassword, updateItemShareLink } from '../services/api'
import type { Project } from '../types/data'
import ImageViewerDialog from './UI/ImageViewerDialog'
import TextViewerDialog from './UI/TextViewerDialog'
import FileViewerDialog from './UI/FileViewerDialog'
import { ImageContextMenu } from './UI/ImageContextMenu'
import { useStorageConfigStore } from '../store/useStorageConfigStore'
import { getOssFileUrl, generateObjectKey } from '../services/ossService'
import { checkLocalUserUuid } from '../services/userManager'
import ShareLinkSelectorDialog from './UI/ShareLinkSelectorDialog'

// 密码验证状态的 localStorage key
const getPasswordStorageKey = (shareId: string) => `share_password_verified_${shareId}`

// 设置类型定义
interface ShareSettings {
  fontSize?: number
  fontFamily?: string
  showBorder?: boolean
  mottoStyle?: boolean
  hideImageBorder?: boolean
}

// ShareItem 类型定义（用于分享页面的项目）
interface ShareItem {
  uuid?: string
  type: 'text' | 'image' | 'file' | 'drawing' | 'connector' | 'video' | 'audio'
  x?: number
  y?: number
  width?: number
  height?: number
  content?: string
  imageId?: string
  imageSrc?: string
  fileId?: string
  fileName?: string
  fileType?: string
  ext?: string // 文件扩展名
  fontSize?: number
  fontFamily?: string
  isWelcomeText?: boolean
  containerFitMode?: 'cover' | 'contain'
  zIndex?: number
  rotation?: number
  shareLink?: string // 分享链接，点击跳转到另一个画布
  // 画笔绘图属性
  drawingData?: {
    paths: Array<{
      id: string
      points: Array<{ x: number; y: number }>
      color: string
      width: number
      opacity?: number
    }>
    backgroundColor?: string
  }
  strokeColor?: string
  strokeWidth?: number
  // 连线属性
  connectorData?: {
    startItemUuid?: string
    endItemUuid?: string
    startPoint: { x: number; y: number }
    endPoint: { x: number; y: number }
    lineType: 'straight' | 'curve' | 'elbow'
    arrowStart?: boolean
    arrowEnd?: boolean
    color?: string
    width?: number
  }
  // 视频/音频属性
  duration?: number
  poster?: string
}

const ShareView = () => {
  const { shareId } = useParams<{ shareId: string }>()
  const navigate = useNavigate()
  const storageConfig = useStorageConfigStore((state) => state.config)
  const isStorageConfigInitialized = useStorageConfigStore((state) => state.isInitialized)
  const { initConfig } = useStorageConfigStore()
  const [project, setProject] = useState<Project | null>(null)
  const [shareInfo, setShareInfo] = useState<{ 
    shareId: string
    createdAt: string
    userUuid?: string
    expiresAt?: string | null
    allowDownload?: boolean
    message?: string | null
    lockInterface?: boolean
  } | null>(null)
  const [settings, setSettings] = useState<ShareSettings>({
    fontSize: 14,
    fontFamily: 'Microsoft YaHei, 微软雅黑, Arial, sans-serif',
    showBorder: false, // 默认不显示文字框边框
    mottoStyle: false,
    hideImageBorder: false
  })
  const [isLoading, setIsLoading] = useState(true)
  const [showLoadingAnimation, setShowLoadingAnimation] = useState(false) // 默认不显示加载动画
  const [loadingAnimationHidden, setLoadingAnimationHidden] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showMessageDialog, setShowMessageDialog] = useState(false)
  
  // 密码验证相关状态
  const [_needPassword, setNeedPassword] = useState(false)
  const [showPasswordDialog, setShowPasswordDialog] = useState(false)
  const [passwordInput, setPasswordInput] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)
  const [showPasswordText, setShowPasswordText] = useState(false)
  const viewportRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const [canvasOffset, setCanvasOffset] = useState({ x: 0, y: 0 })
  const [canvasScale, setCanvasScale] = useState(1)
  const canvasOffsetRef = useRef({ x: 0, y: 0 }) // 用于在事件回调中获取最新值
  const canvasScaleRef = useRef(1) // 用于在事件回调中获取最新值
  const [isPanning, setIsPanning] = useState(false)
  const panStartRef = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 })
  const [isZoomDragging, setIsZoomDragging] = useState(false)
  const zoomSliderRef = useRef<HTMLDivElement>(null)
  
  // 座右铭动画状态（仅针对特定文本）
  const [mottoDisplayText, setMottoDisplayText] = useState('')
  const [isMottoAnimating, setIsMottoAnimating] = useState(false)
  const mottoAnimationRef = useRef<number | null>(null)
  const isFirstLoadRef = useRef(true)
  const isLoadingStartedRef = useRef(false) // 防止重复加载
  // 用于跟踪每个图片尝试的扩展名索引（fileId -> extensionIndex）
  const imageExtensionIndexMap = useRef<Map<string, number>>(new Map())
  
  // 用于触发图片URL重新渲染的状态
  const [imageUrlKey, setImageUrlKey] = useState(0)
  const SPECIAL_MOTTO = '在有限生命里，遇见那个有趣的自己'
  
  // 访问者登录状态（本地有UUID就算有用户）
  const [visitorUserUuid, setVisitorUserUuid] = useState<string | null>(null)
  // 链接选择对话框状态
  const [showLinkSelectorDialog, setShowLinkSelectorDialog] = useState(false)
  const [linkSelectorCurrentLink, setLinkSelectorCurrentLink] = useState<string>('')
  const [_linkSelectorItemIndex, setLinkSelectorItemIndex] = useState<number>(-1)
  
  // 浏览历史状态 - 自己管理历史记录栈
  const [canGoBack, setCanGoBack] = useState(false)
  const [canGoForward, setCanGoForward] = useState(false)
  const historyIndexRef = useRef<number>(-1)  // 当前位置索引
  const historyStackRef = useRef<string[]>([])  // 历史记录栈
  const isNavigatingRef = useRef(false)  // 是否正在导航中（防止重复添加历史记录）
  
  // 统一的查看器状态
  const [viewerState, setViewerState] = useState<{
    type: 'image' | 'text' | 'file' | 'drawing' | 'connector' | 'video' | 'audio' | null
    currentIndex: number
  }>({
    type: null,
    currentIndex: -1
  })

  // 交互功能相关状态
  const [allowItemInteraction, setAllowItemInteraction] = useState(false) // 是否允许操作元素
  const [showSettingsDialog, setShowSettingsDialog] = useState(false) // 显示设置对话框
  const [originalItems, setOriginalItems] = useState<ShareItem[]>([]) // 保存原始状态
  const [activeItemIndex, setActiveItemIndex] = useState<number | null>(null) // 当前正在操作的元素索引
  const [hoveredItemIndex, setHoveredItemIndex] = useState<number | null>(null) // 鼠标悬停的元素索引
  const [_interactionMode, setInteractionMode] = useState<'none' | 'drag' | 'resize' | 'rotate'>('none') // 交互模式
  const [isLocked, setIsLocked] = useState(false) // 本地锁定状态（基于shareInfo.lockInterface，但用户可以切换）
  const [contextMenuState, setContextMenuState] = useState<{ visible: boolean; x: number; y: number; itemIndex: number }>({
    visible: false,
    x: 0,
    y: 0,
    itemIndex: -1
  }) // 右键菜单状态
  const interactionStartRef = useRef<{ x: number; y: number; itemX: number; itemY: number; itemWidth: number; itemHeight: number; itemRotation: number } | null>(null)
  const controlButtonClickedRef = useRef(false) // 跟踪是否点击了控制按钮（旋转、缩放等）

  // 画布过渡动画状态（方案B：在同一组件内切换，并发加载）
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [transitionPhase, setTransitionPhase] = useState<'idle' | 'zoomOut' | 'zoomIn'>('idle')
  const [_pendingShareId, setPendingShareId] = useState<string | null>(null)
  const [_clickedImageRect, setClickedImageRect] = useState<{ x: number; y: number; width: number; height: number } | null>(null)
  
  // 临时提示状态（用于显示不阻塞的提示信息）
  const [tempTip, setTempTip] = useState<string | null>(null)
  const tempTipTimerRef = useRef<number | null>(null)
  
  // 显示临时提示（自动消失）
  const showTempTip = (message: string, duration: number = 3000) => {
    if (tempTipTimerRef.current) {
      clearTimeout(tempTipTimerRef.current)
    }
    setTempTip(message)
    tempTipTimerRef.current = window.setTimeout(() => {
      setTempTip(null)
      tempTipTimerRef.current = null
    }, duration)
  }
  
  // 跳转链接时需要输入密码的目标 shareId
  const [pendingPasswordShareId, setPendingPasswordShareId] = useState<string | null>(null)

  // 同步 canvasOffset 和 canvasScale 到 ref（用于事件回调中获取最新值）
  // 使用 useLayoutEffect 确保在用户交互之前同步完成
  useLayoutEffect(() => {
    canvasOffsetRef.current = canvasOffset
  }, [canvasOffset])

  useLayoutEffect(() => {
    canvasScaleRef.current = canvasScale
  }, [canvasScale])

  // 计算所有元素的边界
  const getItemsBounds = () => {
    if (!project || !project.items || project.items.length === 0) {
      return null
    }

    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity

    project.items.forEach(item => {
      const x = item.x || 0
      const y = item.y || 0
      const width = item.width || 300
      const height = item.height || 200

      minX = Math.min(minX, x)
      minY = Math.min(minY, y)
      maxX = Math.max(maxX, x + width)
      maxY = Math.max(maxY, y + height)
    })

    return { minX, minY, maxX, maxY }
  }

  // 定位到所有元素中心
  const handleCenterViewOnAllItems = (e?: React.MouseEvent | React.TouchEvent) => {
    e?.preventDefault()
    e?.stopPropagation()
    
    console.log('[handleCenterViewOnAllItems] 开始定位')
    
    if (!viewportRef.current) {
      console.warn('[handleCenterViewOnAllItems] viewportRef.current 不存在')
      return
    }
    
    if (!project) {
      console.warn('[handleCenterViewOnAllItems] project 不存在')
      return
    }

    const bounds = getItemsBounds()
    if (!bounds) {
      console.log('[handleCenterViewOnAllItems] 没有元素，重置到默认位置')
      // 如果没有元素，重置到默认位置
      setCanvasOffset({ x: 0, y: 0 })
      setCanvasScale(1)
      return
    }

    console.log('[handleCenterViewOnAllItems] 元素边界:', bounds)

    // 计算所有元素的中心点（世界坐标）
    const centerX = (bounds.minX + bounds.maxX) / 2
    const centerY = (bounds.minY + bounds.maxY) / 2

    // 计算元素的宽度和高度
    const itemsWidth = bounds.maxX - bounds.minX
    const itemsHeight = bounds.maxY - bounds.minY

    // 获取viewport的尺寸
    const viewport = viewportRef.current
    const viewportWidth = viewport.clientWidth
    const viewportHeight = viewport.clientHeight

    console.log('[handleCenterViewOnAllItems] 视口尺寸:', { viewportWidth, viewportHeight })
    console.log('[handleCenterViewOnAllItems] 元素尺寸:', { itemsWidth, itemsHeight })
    console.log('[handleCenterViewOnAllItems] 元素中心:', { centerX, centerY })

    // 计算需要的缩放比例，使所有元素都能显示在窗口中（留一些边距）
    const padding = 80 // 边距
    const scaleX = (viewportWidth - padding * 2) / itemsWidth
    const scaleY = (viewportHeight - padding * 2) / itemsHeight
    
    // 选择较小的缩放比例，确保所有元素都能显示
    let newScale = Math.min(scaleX, scaleY)
    
    // 限制缩放范围（最小0.1，最大5）
    newScale = Math.max(0.1, Math.min(5, newScale))
    
    // 如果元素很小，不需要缩小，保持合适的缩放（不小于1.0）
    if (itemsWidth < viewportWidth && itemsHeight < viewportHeight) {
      // 元素都在窗口内，使用合适的缩放（不小于1.0）
      newScale = Math.max(1.0, newScale)
    }

    // 计算新的offset，使元素中心对齐到viewport中心
    // 公式：screenX = worldX * scale + offset
    // 所以：offset = screenX - worldX * scale
    const newOffsetX = viewportWidth / 2 - centerX * newScale
    const newOffsetY = viewportHeight / 2 - centerY * newScale

    console.log('[handleCenterViewOnAllItems] 新的偏移和缩放:', { 
      newOffsetX, 
      newOffsetY, 
      newScale 
    })

    setCanvasOffset({ x: newOffsetX, y: newOffsetY })
    setCanvasScale(newScale)
    
    console.log('[handleCenterViewOnAllItems] 定位完成')
  }

  // 缩放滑块点击处理
  const handleZoomSliderClick = (e: React.MouseEvent) => {
    if (!zoomSliderRef.current || !viewportRef.current) return
    const track = zoomSliderRef.current.querySelector('div[style*="height"]') as HTMLElement
    if (!track) return
    
    const trackRect = track.getBoundingClientRect()
    const clickY = e.clientY - trackRect.top
    const percentage = Math.max(0, Math.min(1, clickY / trackRect.height))
    
    // 反转百分比（顶部是最大缩放，底部是最小缩放）
    const newScale = 0.1 + (1 - percentage) * (5 - 0.1)
    
    // 以视口中心为基准进行缩放
    const viewport = viewportRef.current
    const viewportRect = viewport.getBoundingClientRect()
    const centerX = viewportRect.width / 2
    const centerY = viewportRect.height / 2
    
    // 计算世界坐标系中的中心点位置
    const worldX = (centerX - canvasOffset.x) / canvasScale
    const worldY = (centerY - canvasOffset.y) / canvasScale
    
    // 计算新的偏移量，使中心点保持不变
    const newOffsetX = centerX - worldX * newScale
    const newOffsetY = centerY - worldY * newScale
    
    canvasOffsetRef.current = { x: newOffsetX, y: newOffsetY }
    canvasScaleRef.current = newScale

    setCanvasOffset({ x: newOffsetX, y: newOffsetY })
    setCanvasScale(newScale)
  }

  // 缩放滑块触摸点击处理
  const handleZoomSliderTouch = (e: React.TouchEvent) => {
    if (!zoomSliderRef.current || !viewportRef.current) return
    const track = zoomSliderRef.current.querySelector('div[style*="height"]') as HTMLElement
    if (!track) return
    
    const trackRect = track.getBoundingClientRect()
    const touch = e.touches[0]
    const clickY = touch.clientY - trackRect.top
    const percentage = Math.max(0, Math.min(1, clickY / trackRect.height))
    
    // 反转百分比（顶部是最大缩放，底部是最小缩放）
    const newScale = 0.1 + (1 - percentage) * (5 - 0.1)
    
    // 以视口中心为基准进行缩放
    const viewport = viewportRef.current
    const viewportRect = viewport.getBoundingClientRect()
    const centerX = viewportRect.width / 2
    const centerY = viewportRect.height / 2
    
    // 计算世界坐标系中的中心点位置
    const worldX = (centerX - canvasOffset.x) / canvasScale
    const worldY = (centerY - canvasOffset.y) / canvasScale
    
    // 计算新的偏移量，使中心点保持不变
    const newOffsetX = centerX - worldX * newScale
    const newOffsetY = centerY - worldY * newScale
    
    canvasOffsetRef.current = { x: newOffsetX, y: newOffsetY }
    canvasScaleRef.current = newScale

    setCanvasOffset({ x: newOffsetX, y: newOffsetY })
    setCanvasScale(newScale)
  }

  // 缩放滑块拖拽处理（鼠标）
  const handleZoomSliderDrag = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    // 记录初始状态，用于稳定计算
    const viewport = viewportRef.current
    if (!viewport) return
    const viewportRect = viewport.getBoundingClientRect()
    const centerX = viewportRect.width / 2
    const centerY = viewportRect.height / 2
    
    // 获取拖拽开始时的 offset 和 scale
    const startOffset = canvasOffset
    const startScale = canvasScale
    
    // 计算不变的世界中心点
    const worldCenterX = (centerX - startOffset.x) / startScale
    const worldCenterY = (centerY - startOffset.y) / startScale
    
    const handleMove = (moveEvent: MouseEvent) => {
      if (!zoomSliderRef.current || !viewportRef.current) return
      const track = zoomSliderRef.current.querySelector('div[style*="height"]') as HTMLElement
      if (!track) return
      
      const trackRect = track.getBoundingClientRect()
      const mouseY = moveEvent.clientY - trackRect.top
      const percentage = Math.max(0, Math.min(1, mouseY / trackRect.height))
      
      // 反转百分比（顶部是最大缩放，底部是最小缩放）
      const newScale = 0.1 + (1 - percentage) * (5 - 0.1)
      
      // 使用初始世界中心点计算新的偏移量
      const newOffsetX = centerX - worldCenterX * newScale
      const newOffsetY = centerY - worldCenterY * newScale
      
      // 更新 ref
      canvasOffsetRef.current = { x: newOffsetX, y: newOffsetY }
      canvasScaleRef.current = newScale
      
      setCanvasOffset({ x: newOffsetX, y: newOffsetY })
      setCanvasScale(newScale)
    }
    
    const handleUp = () => {
      window.removeEventListener('mousemove', handleMove)
      window.removeEventListener('mouseup', handleUp)
      setIsZoomDragging(false)
    }
    
    window.addEventListener('mousemove', handleMove)
    window.addEventListener('mouseup', handleUp)
  }

  // 缩放滑块拖拽处理（触摸）
  const handleZoomSliderTouchDrag = (e: React.TouchEvent) => {
    e.stopPropagation()
    
    // 记录初始状态
    const viewport = viewportRef.current
    if (!viewport) return
    const viewportRect = viewport.getBoundingClientRect()
    const centerX = viewportRect.width / 2
    const centerY = viewportRect.height / 2
    
    const startOffset = canvasOffset
    const startScale = canvasScale
    
    const worldCenterX = (centerX - startOffset.x) / startScale
    const worldCenterY = (centerY - startOffset.y) / startScale
    
    const handleMove = (moveEvent: TouchEvent) => {
      if (!zoomSliderRef.current || !viewportRef.current) return
      const track = zoomSliderRef.current.querySelector('div[style*="height"]') as HTMLElement
      if (!track) return
      
      if (moveEvent.touches.length === 0) return
      const trackRect = track.getBoundingClientRect()
      const touchY = moveEvent.touches[0].clientY - trackRect.top
      const percentage = Math.max(0, Math.min(1, touchY / trackRect.height))
      
      const newScale = 0.1 + (1 - percentage) * (5 - 0.1)
      
      const newOffsetX = centerX - worldCenterX * newScale
      const newOffsetY = centerY - worldCenterY * newScale
      
      // 更新 Ref
      canvasOffsetRef.current = { x: newOffsetX, y: newOffsetY }
      canvasScaleRef.current = newScale

      setCanvasOffset({ x: newOffsetX, y: newOffsetY })
      setCanvasScale(newScale)
    }
    
    const handleUp = () => {
      window.removeEventListener('touchmove', handleMove)
      window.removeEventListener('touchend', handleUp)
      setIsZoomDragging(false)
    }
    
    window.addEventListener('touchmove', handleMove, { passive: false })
    window.addEventListener('touchend', handleUp, { passive: false })
  }

  // 获取所有可查看的项目索引（排除欢迎文本）
  const getViewableItems = (): number[] => {
    if (!project || !project.items) return []
    return project.items
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => {
        // 排除欢迎文本
        if (item.type === 'text' && (item as ShareItem).isWelcomeText) {
          return false
        }
        // 只包含有弹窗的元素类型：image、text、file
        // 排除 drawing、connector、video、audio（这些类型没有对应的弹窗组件）
        return item.type === 'image' || item.type === 'text' || item.type === 'file'
      })
      .map(({ index }) => index)
  }

  const viewableItems = getViewableItems()

  // 获取当前查看的项目
  const getCurrentViewItem = (): ShareItem | null => {
    if (viewerState.type === null || viewerState.currentIndex < 0) return null
    const itemIndex = viewableItems[viewerState.currentIndex]
    if (itemIndex === undefined || !project?.items) return null
    return project.items[itemIndex] as ShareItem
  }

  // 切换查看器
  const handleOpenViewer = (itemIndex: number) => {
    const viewableIndex = viewableItems.indexOf(itemIndex)
    if (viewableIndex === -1) return
    
    const item = project?.items?.[itemIndex] as ShareItem
    if (!item) return

    setViewerState({
      type: item.type,
      currentIndex: viewableIndex
    })
  }

  const handleCloseViewer = () => {
    setViewerState({
      type: null,
      currentIndex: -1
    })
  }

  // 计算画布居中的偏移量
  const calculateCenterOffset = (items: ShareItem[], scale: number) => {
    if (!items || items.length === 0 || !viewportRef.current) {
      return { x: 0, y: 0 }
    }

    // 计算所有元素的边界
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity

    items.forEach(item => {
      const x = item.x || 0
      const y = item.y || 0
      const width = item.width || 300
      const height = item.height || 200

      minX = Math.min(minX, x)
      minY = Math.min(minY, y)
      maxX = Math.max(maxX, x + width)
      maxY = Math.max(maxY, y + height)
    })

    // 计算内容中心点
    const contentCenterX = (minX + maxX) / 2
    const contentCenterY = (minY + maxY) / 2

    // 获取视口中心
    const viewport = viewportRef.current
    const viewportCenterX = viewport.clientWidth / 2
    const viewportCenterY = viewport.clientHeight / 2

    // 计算偏移量，使内容中心对齐视口中心
    const offsetX = viewportCenterX - contentCenterX * scale
    const offsetY = viewportCenterY - contentCenterY * scale

    return { x: offsetX, y: offsetY }
  }

  // 处理点击有分享链接的图片 - 执行画布过渡动画（方案B：在同一组件内切换）
  const handleShareLinkClick = (link: string, imageElement?: HTMLElement) => {
    if (!link || isTransitioning) return
    
    // 解析链接，提取 shareId
    let targetShareId = ''
    if (link.startsWith('/share/')) {
      targetShareId = link.replace('/share/', '')
    } else if (link.includes('/share/')) {
      // 完整URL
      const match = link.match(/\/share\/([^/?#]+)/)
      if (match) {
        targetShareId = match[1]
      }
    } else {
      // 可能直接是 shareId
      targetShareId = link
    }
    
    if (!targetShareId) return
    
    // 获取点击图片的位置信息（用于动画起点）
    if (imageElement) {
      const rect = imageElement.getBoundingClientRect()
      setClickedImageRect({
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height
      })
    }
    
    // 开始放大动画（画布放大并淡出）
    setIsTransitioning(true)
    setTransitionPhase('zoomOut')
    setPendingShareId(targetShareId)
    
    // 检查 localStorage 中是否有已验证的密码
    const storageKey = getPasswordStorageKey(targetShareId)
    const stored = localStorage.getItem(storageKey)
    let cachedPassword: string | undefined
    if (stored) {
      try {
        const { password, expireTime } = JSON.parse(stored)
        if (Date.now() < expireTime) {
          cachedPassword = password
        } else {
          // 密码过期，清除缓存
          localStorage.removeItem(storageKey)
        }
      } catch {
        localStorage.removeItem(storageKey)
      }
    }
    
    // 并发：立即开始加载新数据（不等待动画完成），带上缓存的密码
    // 通过画布内链接跳转时不增加访问次数（incrementViews: false）
    const loadPromise = fetch(`/api/share/project/${targetShareId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ password: cachedPassword, incrementViews: false })
    }).then(async response => {
      const result = await response.json()
      
      if (response.status === 404) {
        return { error: 'not_found', message: '分享链接不存在或已过期' }
      }
      
      // 如果需要密码但密码错误或未提供
      if (response.status === 401 && result.requirePassword) {
        return { error: 'need_password', message: '需要输入密码' }
      }
      
      return result
    })
    
    // 等待动画完成和数据加载都完成（提前100ms切换，让飞出和淡入重叠）
    const animationPromise = new Promise(resolve => setTimeout(resolve, 600))
    
    Promise.all([loadPromise, animationPromise])
      .then(([result]) => {
        // 处理错误情况
        if (result.error === 'not_found') {
          // 不存在的画布：显示当前画布，给出提示
          setIsTransitioning(false)
          setTransitionPhase('idle')
          setPendingShareId(null)
          setClickedImageRect(null)
          showTempTip('链接的画布不存在或已过期')
          return
        }
        
        if (result.error === 'need_password') {
          // 加密画布：显示密码输入对话框
          setIsTransitioning(false)
          setTransitionPhase('idle')
          setPendingShareId(null)
          setClickedImageRect(null)
          // 设置待验证的 shareId，显示密码对话框
          setPendingPasswordShareId(targetShareId)
          setPasswordInput('')
          setPasswordError('')
          setShowPasswordDialog(true)
          return
        }
        
        if (result.success && result.data) {
          const newProject = result.data
          
          // 更新 shareInfo
          if (result.shareInfo) {
            setShareInfo(result.shareInfo)
            const shouldLock = result.shareInfo.lockInterface !== false
            setIsLocked(shouldLock)
            if (!shouldLock) {
              setAllowItemInteraction(true)
            }
          }
          
          // 更新设置
          if (result.settings) {
            setSettings({
              fontSize: result.settings.fontSize || 14,
              fontFamily: result.settings.fontFamily || 'Microsoft YaHei, 微软雅黑, Arial, sans-serif',
              showBorder: result.settings.showBorder !== undefined ? result.settings.showBorder : true,
              mottoStyle: result.settings.mottoStyle || false,
              hideImageBorder: result.settings.hideImageBorder || false
            })
          }
          
          // 确保项目有必要的属性
          if (!newProject.canvasOffset) {
            newProject.canvasOffset = { x: 0, y: 0 }
          }
          if (!newProject.canvasScale) {
            newProject.canvasScale = 1
          }
          if (!newProject.items) {
            newProject.items = []
          }
          
          // 清除图片扩展名缓存
          imageExtensionIndexMap.current.clear()
          
          // 计算居中偏移量
          const scale = newProject.canvasScale
          const centerOffset = calculateCenterOffset(newProject.items as ShareItem[], scale)
          
          // 更新项目数据
          setProject(newProject)
          setOriginalItems(JSON.parse(JSON.stringify(newProject.items || [])) as ShareItem[])
          
          // 设置居中的画布位置
          setCanvasOffset(centerOffset)
          setCanvasScale(scale)
          
          // 添加到历史记录
          addToHistory(targetShareId)
          
          // 更新URL（使用 pushState 创建新的浏览器历史记录）
          window.history.pushState({ shareId: targetShareId }, '', `/share/${targetShareId}`)
          
          // 开始缩小动画（新画布从远处放大进入）
          setTransitionPhase('zoomIn')
          
          // 动画完成后重置状态
          setTimeout(() => {
            setIsTransitioning(false)
            setTransitionPhase('idle')
            setPendingShareId(null)
            setClickedImageRect(null)
            updateNavigationState()
          }, 800)
          
        } else {
          // 加载失败，重置状态，显示当前画布
          setIsTransitioning(false)
          setTransitionPhase('idle')
          setPendingShareId(null)
          setClickedImageRect(null)
          showTempTip('加载画布失败')
          console.error('加载新画布失败:', result.message)
        }
      })
      .catch(err => {
        console.error('加载新画布出错:', err)
        setIsTransitioning(false)
        setTransitionPhase('idle')
        setPendingShareId(null)
        setClickedImageRect(null)
        showTempTip('加载画布出错')
      })
  }

  const handlePrevViewer = () => {
    if (viewerState.currentIndex > 0) {
      const newIndex = viewerState.currentIndex - 1
      const itemIndex = viewableItems[newIndex]
      const item = project?.items?.[itemIndex] as ShareItem
      if (item) {
        setViewerState({
          type: item.type,
          currentIndex: newIndex
        })
      }
    }
  }

  const handleNextViewer = () => {
    if (viewerState.currentIndex < viewableItems.length - 1) {
      const newIndex = viewerState.currentIndex + 1
      const itemIndex = viewableItems[newIndex]
      const item = project?.items?.[itemIndex] as ShareItem
      if (item) {
        setViewerState({
          type: item.type,
          currentIndex: newIndex
        })
      }
    }
  }

  // 保存分享链接到访问者自己的项目中
  const handleSaveShareLink = async (currentItem: ShareItem, link: string) => {
    // 检查访问者是否有UUID
    if (!visitorUserUuid) {
      alert('本地没有用户UUID，请先访问主页面创建用户。')
      return
    }

    if (!project || !shareInfo) {
      alert('项目数据未加载')
      return
    }
    
    // 使用 API 更新项目数据（只更新链接）
    // targetUserUuid 是项目的拥有者（shareInfo.userUuid）
    // projectUuid 是当前项目 UUID
    // itemUuid 是当前 Item UUID
    
    try {
        const itemUuid = currentItem.uuid || (currentItem as any).id
        if (!itemUuid) {
            alert('无法识别图片ID')
            return
        }

        const response = await updateItemShareLink(
            shareInfo.userUuid || '', // 目标用户（项目拥有者）
            project.uuid || '',       // 项目ID
            itemUuid,           // Item ID
            link                // 新链接
        )
        
        if (response.success) {
            // 更新本地状态以反映更改
            // 手动更新本地 project state，以便 UI 立即刷新
            const updatedProject = { ...project }
            let itemFound = false
            
            if (updatedProject.items) {
                const itemIndex = updatedProject.items.findIndex(it => 
                    it.uuid === itemUuid || (it as any).id === itemUuid
                )
                if (itemIndex !== -1) {
                    updatedProject.items[itemIndex] = {
                        ...updatedProject.items[itemIndex],
                        shareLink: link
                    }
                    itemFound = true
                }
            }
            
            if (!itemFound && updatedProject.groups) {
                for (const group of updatedProject.groups) {
                    if (group.items) {
                        const itemIndex = group.items.findIndex(it => 
                            it.uuid === itemUuid || (it as any).id === itemUuid
                        )
                        if (itemIndex !== -1) {
                            group.items[itemIndex] = {
                                ...group.items[itemIndex],
                                shareLink: link
                            }
                            itemFound = true
                            break
                        }
                    }
                }
            }
            
            setProject(updatedProject)
            setShowLinkSelectorDialog(false)
            alert('链接设置成功！')
        } else {
            throw new Error(response.message || '保存失败')
        }
    } catch (error: any) {
        console.error('保存链接失败:', error)
        alert(`保存链接失败: ${error.message || '可能是权限不足或网络问题'}`)
    }
  }

  // 初始化存储配置（只执行一次）
  useEffect(() => {
    initConfig().catch(err => {
      console.error('初始化存储配置失败:', err)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 检查访问者登录状态
  useEffect(() => {
    // 使用异步检查函数，覆盖所有可能的存储位置（localStorage 和 IndexedDB）
    checkLocalUserUuid().then(userUuid => {
      setVisitorUserUuid(userUuid)
    })
  }, [])

  // 生成文件 URL 的辅助函数
  const getFileUrl = (fileId: string, userUuid?: string, projectUuid?: string, extension?: string): string => {
    if (!fileId || !userUuid || !projectUuid) {
      return ''
    }

    // 如果 fileId 没有扩展名，使用提供的 extension 或默认 .png（兼容旧数据）
    let finalFileId = fileId
    if (!fileId.includes('.')) {
      finalFileId = extension ? `${fileId}.${extension}` : `${fileId}.png`
    }

    // 如果使用云存储，生成云存储 URL
    if (storageConfig?.useOSS && storageConfig.bucket && storageConfig.region) {
      const objectKey = generateObjectKey(userUuid, projectUuid, finalFileId)
      const provider = storageConfig.provider || 'aliyun'
      return getOssFileUrl(objectKey, storageConfig.bucket, storageConfig.region, storageConfig.endpoint || undefined, provider)
    }

    // 否则使用后端 URL
    return `/api/files/${userUuid}/${projectUuid}/${finalFileId}`
  }

  // 加载项目数据（带密码验证）
  const loadProjectWithPassword = async (password?: string, incrementViews: boolean = true) => {
    if (!shareId) return
    
    try {
      setIsLoading(true)
      const response = await fetch(`/api/share/project/${shareId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password, incrementViews })
      })
      
      // 先检查响应状态，如果是404，直接返回友好提示
      if (response.status === 404) {
        setError('您访问的分享链接不存在，或者已过期')
        setIsLoading(false)
        return false
      }
      
      const result = await response.json()
      
      // 如果需要密码但密码错误
      if (response.status === 401 && result.requirePassword) {
        setPasswordError('密码错误，请重新输入')
        setIsVerifying(false)
        return false
      }
      
      if (result.success && result.data) {
        const shareProject = result.data
        // 保存shareInfo（包含userUuid、expiresAt、allowDownload、message、lockInterface）
        if (result.shareInfo) {
          setShareInfo(result.shareInfo)
          // 初始化锁定状态：
          // - 如果 lockInterface 为 false（不锁定），则默认未锁定（用户可以切换）
          // - 如果 lockInterface 为 true 或 undefined（锁定），则默认锁定
          const shouldLock = result.shareInfo.lockInterface !== false
          setIsLocked(shouldLock)
          // 如果不锁定，允许交互
          if (!shouldLock) {
            setAllowItemInteraction(true)
          }
          
          // 检查访问期限
          if (result.shareInfo.expiresAt) {
            const expiresAt = new Date(result.shareInfo.expiresAt)
            const now = new Date()
            if (now > expiresAt) {
              setError('分享链接已过期')
              setIsLoading(false)
              return false
            }
          }
          
          // 如果有提示信息，显示对话框
          if (result.shareInfo.message) {
            setShowMessageDialog(true)
          }
        }
        
        // 保存设置信息
        if (result.settings) {
          setSettings({
            fontSize: result.settings.fontSize || 14,
            fontFamily: result.settings.fontFamily || 'Microsoft YaHei, 微软雅黑, Arial, sans-serif',
            showBorder: result.settings.showBorder !== undefined ? result.settings.showBorder : true,
            mottoStyle: result.settings.mottoStyle || false,
            hideImageBorder: result.settings.hideImageBorder || false
          })
        }
        
        // 确保项目有必要的属性
        if (!shareProject.canvasOffset) {
          shareProject.canvasOffset = { x: 0, y: 0 }
        }
        if (!shareProject.canvasScale) {
          shareProject.canvasScale = 1
        }
        if (!shareProject.items) {
          shareProject.items = []
        }
        setProject(shareProject)
        
        // 保存原始状态（深拷贝）
        const itemsCopy = JSON.parse(JSON.stringify(shareProject.items || [])) as ShareItem[]
        setOriginalItems(itemsCopy)
        
        // 密码验证成功，保存到 localStorage（24小时有效）
        if (password && shareId) {
          const storageKey = getPasswordStorageKey(shareId)
          const expireTime = Date.now() + 24 * 60 * 60 * 1000 // 24小时
          localStorage.setItem(storageKey, JSON.stringify({ password, expireTime }))
        }
        
        // 显示加载动画
        setShowLoadingAnimation(true)
        
        return true
      } else {
        setError('分享链接无效或已过期')
        return false
      }
    } catch (err: any) {
      // 检查是否是404错误
      if (err.response?.status === 404 || err.message?.includes('404')) {
        setError('您访问的分享链接不存在，或者已过期')
      } else {
        setError(err.message || '加载分享内容失败')
      }
      return false
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!shareId) {
      setError('分享链接无效')
      setIsLoading(false)
      return
    }

    // 等待存储配置初始化完成
    if (!isStorageConfigInitialized) {
      return
    }

    // 防止 React.StrictMode 导致的重复加载
    if (isLoadingStartedRef.current) {
      return
    }
    isLoadingStartedRef.current = true

    const checkAndLoadProject = async () => {
      try {
        // 先检查是否需要密码
        const checkResult = await checkSharePassword(shareId)
        
        if (!checkResult.success) {
          setError('分享链接不存在')
          setIsLoading(false)
          return
        }
        
        const { hasPassword, expiresAt } = checkResult.data
        
        // 检查是否过期
        if (expiresAt) {
          const expireDate = new Date(expiresAt)
          if (new Date() > expireDate) {
            setError('分享链接已过期')
            setIsLoading(false)
            return
          }
        }
        
        if (hasPassword) {
          setNeedPassword(true)
          
          // 检查 localStorage 中是否有已验证的密码
          const storageKey = getPasswordStorageKey(shareId)
          const stored = localStorage.getItem(storageKey)
          
          if (stored) {
            try {
              const { password, expireTime } = JSON.parse(stored)
              if (Date.now() < expireTime) {
                // 密码未过期，尝试使用缓存的密码加载
                const success = await loadProjectWithPassword(password)
                if (success) {
                  setShowPasswordDialog(false)
                  return
                }
              }
              // 密码过期或无效，清除缓存
              localStorage.removeItem(storageKey)
            } catch {
              localStorage.removeItem(storageKey)
            }
          }
          
          // 需要用户输入密码
          setShowPasswordDialog(true)
          setIsLoading(false)
        } else {
          // 不需要密码，直接加载
          await loadProjectWithPassword()
        }
      } catch (err: any) {
        // 检查是否是404错误
        if (err.response?.status === 404 || err.message?.includes('404')) {
          setError('您访问的分享链接不存在，或者已过期')
        } else {
          setError(err.message || '加载分享内容失败')
        }
        setIsLoading(false)
      }
    }

    checkAndLoadProject()
  }, [shareId, isStorageConfigInitialized])

  // 处理浏览器回退/前进按钮
  // 更新前进后退按钮状态
  const updateNavigationState = () => {
    const canBack = historyIndexRef.current > 0
    const canForward = historyIndexRef.current < historyStackRef.current.length - 1
    
    setCanGoBack(canBack)
    setCanGoForward(canForward)
  }

  // 添加到历史记录
  const addToHistory = (newShareId: string) => {
    if (isNavigatingRef.current) return  // 导航中不添加
    
    const currentIndex = historyIndexRef.current
    const currentShareId = historyStackRef.current[currentIndex]
    
    // 如果和当前的一样，不添加
    if (currentShareId === newShareId) return
    
    // 清除当前位置之后的所有历史记录（前进历史）
    historyStackRef.current = historyStackRef.current.slice(0, currentIndex + 1)
    // 添加新的shareId
    historyStackRef.current.push(newShareId)
    historyIndexRef.current = historyStackRef.current.length - 1
    
    console.log('[History] Added:', newShareId, 'Stack:', historyStackRef.current, 'Index:', historyIndexRef.current)
    updateNavigationState()
  }

  // 导航到指定的 shareId（用于前进后退）
  const navigateToShareId = async (targetShareId: string, direction: 'back' | 'forward') => {
    if (!targetShareId || isTransitioning) return
    
    console.log('[Navigation]', direction, 'to:', targetShareId)
    
    // 标记为正在导航
    isNavigatingRef.current = true
    
    // 开始过渡动画
    setIsTransitioning(true)
    setTransitionPhase('zoomOut')
    
    // 检查 localStorage 中是否有已验证的密码
    const storageKey = getPasswordStorageKey(targetShareId)
    const stored = localStorage.getItem(storageKey)
    let cachedPassword: string | undefined
    if (stored) {
      try {
        const { password, expireTime } = JSON.parse(stored)
        if (Date.now() < expireTime) {
          cachedPassword = password
        } else {
          localStorage.removeItem(storageKey)
        }
      } catch {
        localStorage.removeItem(storageKey)
      }
    }
    
    try {
      // 加载画布数据
      const response = await fetch(`/api/share/project/${targetShareId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password: cachedPassword, incrementViews: false })
      })
      
      const result = await response.json()
      
      if (response.status === 404) {
        showTempTip('画布不存在或已过期')
        setIsTransitioning(false)
        setTransitionPhase('idle')
        isNavigatingRef.current = false
        return
      }
      
      if (response.status === 401 && result.requirePassword) {
        // 需要密码，显示密码对话框
        setIsTransitioning(false)
        setTransitionPhase('idle')
        setPendingPasswordShareId(targetShareId)
        setPasswordInput('')
        setPasswordError('')
        setShowPasswordDialog(true)
        isNavigatingRef.current = false
        return
      }
      
      if (result.success && result.data) {
        const newProject = result.data
        
        // 更新 shareInfo
        if (result.shareInfo) {
          setShareInfo(result.shareInfo)
          const shouldLock = result.shareInfo.lockInterface !== false
          setIsLocked(shouldLock)
          if (!shouldLock) {
            setAllowItemInteraction(true)
          }
        }
        
        // 更新设置
        if (result.settings) {
          setSettings({
            fontSize: result.settings.fontSize || 14,
            fontFamily: result.settings.fontFamily || 'Microsoft YaHei, 微软雅黑, Arial, sans-serif',
            showBorder: result.settings.showBorder !== undefined ? result.settings.showBorder : true,
            mottoStyle: result.settings.mottoStyle || false,
            hideImageBorder: result.settings.hideImageBorder || false
          })
        }
        
        // 确保项目有必要的属性
        if (!newProject.canvasOffset) {
          newProject.canvasOffset = { x: 0, y: 0 }
        }
        if (!newProject.canvasScale) {
          newProject.canvasScale = 1
        }
        if (!newProject.items) {
          newProject.items = []
        }
        
        // 清除图片扩展名缓存
        imageExtensionIndexMap.current.clear()
        
        // 计算居中偏移量
        const scale = newProject.canvasScale
        const centerOffset = calculateCenterOffset(newProject.items as ShareItem[], scale)
        
        // 更新项目数据
        setProject(newProject)
        setOriginalItems(JSON.parse(JSON.stringify(newProject.items || [])) as ShareItem[])
        
        // 设置居中的画布位置
        setCanvasOffset(centerOffset)
        setCanvasScale(scale)
        
        // 更新URL（使用 replaceState，因为我们自己管理历史记录栈）
        window.history.replaceState({ shareId: targetShareId }, '', `/share/${targetShareId}`)
        
        // 开始缩小动画
        setTransitionPhase('zoomIn')
        
        // 动画完成后重置状态
        setTimeout(() => {
          setIsTransitioning(false)
          setTransitionPhase('idle')
          isNavigatingRef.current = false
          updateNavigationState()
        }, 800)
        
      } else {
        showTempTip('加载画布失败')
        setIsTransitioning(false)
        setTransitionPhase('idle')
        isNavigatingRef.current = false
      }
    } catch (err) {
      console.error('加载画布出错:', err)
      showTempTip('加载画布出错')
      setIsTransitioning(false)
      setTransitionPhase('idle')
      isNavigatingRef.current = false
    }
  }

  // 初始化历史记录 - 当 shareId 变化时添加到历史
  useEffect(() => {
    if (shareId && !isNavigatingRef.current) {
      addToHistory(shareId)
    }
  }, [shareId])

  // 监听浏览器的前进后退按钮（popstate）
  useEffect(() => {
    const handlePopState = (_event: PopStateEvent) => {
      // 当用户点击浏览器回退/前进按钮时
      const currentPath = window.location.pathname
      const match = currentPath.match(/\/share\/([^/?#]+)/)
      
      if (match) {
        const targetShareId = match[1]
        
        if (targetShareId !== shareId) {
          // 找到目标在历史记录中的位置
          const targetIndex = historyStackRef.current.indexOf(targetShareId)
          const currentIndex = historyIndexRef.current
          
          if (targetIndex !== -1 && targetIndex !== currentIndex) {
            // 确定方向
            const direction = targetIndex < currentIndex ? 'back' : 'forward'
            // 更新索引
            historyIndexRef.current = targetIndex
            // 导航到目标画布
            navigateToShareId(targetShareId, direction)
          }
        }
      }
      
      updateNavigationState()
    }

    window.addEventListener('popstate', handlePopState)
    
    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [shareId])

  // 处理后退按钮点击
  const handleGoBack = () => {
    if (!canGoBack || isTransitioning) return
    
    const targetIndex = historyIndexRef.current - 1
    if (targetIndex >= 0) {
      const targetShareId = historyStackRef.current[targetIndex]
      if (targetShareId) {
        // 先更新索引
        historyIndexRef.current = targetIndex
        navigateToShareId(targetShareId, 'back')
      }
    }
  }

  // 处理前进按钮点击
  const handleGoForward = () => {
    if (!canGoForward || isTransitioning) return
    
    const targetIndex = historyIndexRef.current + 1
    if (targetIndex < historyStackRef.current.length) {
      const targetShareId = historyStackRef.current[targetIndex]
      if (targetShareId) {
        // 先更新索引
        historyIndexRef.current = targetIndex
        navigateToShareId(targetShareId, 'forward')
      }
    }
  }

  // 处理密码提交
  const handlePasswordSubmit = async () => {
    if (!passwordInput.trim()) {
      setPasswordError('请输入密码')
      return
    }
    
    setIsVerifying(true)
    setPasswordError('')
    
    // 如果是跳转链接时需要密码验证（不增加访问次数）
    if (pendingPasswordShareId) {
      try {
        const response = await fetch(`/api/share/project/${pendingPasswordShareId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ password: passwordInput, incrementViews: false })
        })
        
        const result = await response.json()
        
        if (response.status === 401 && result.requirePassword) {
          setPasswordError('密码错误，请重新输入')
          setIsVerifying(false)
          return
        }
        
        if (response.status === 404) {
          setPasswordError('分享链接不存在')
          setIsVerifying(false)
          return
        }
        
        if (result.success && result.data) {
          // 密码验证成功，保存到 localStorage（24小时有效）
          const storageKey = getPasswordStorageKey(pendingPasswordShareId)
          const expireTime = Date.now() + 24 * 60 * 60 * 1000
          localStorage.setItem(storageKey, JSON.stringify({ password: passwordInput, expireTime }))
          
          const newProject = result.data
          
          // 更新 shareInfo
          if (result.shareInfo) {
            setShareInfo(result.shareInfo)
            const shouldLock = result.shareInfo.lockInterface !== false
            setIsLocked(shouldLock)
            if (!shouldLock) {
              setAllowItemInteraction(true)
            }
          }
          
          // 更新设置
          if (result.settings) {
            setSettings({
              fontSize: result.settings.fontSize || 14,
              fontFamily: result.settings.fontFamily || 'Microsoft YaHei, 微软雅黑, Arial, sans-serif',
              showBorder: result.settings.showBorder !== undefined ? result.settings.showBorder : true,
              mottoStyle: result.settings.mottoStyle || false,
              hideImageBorder: result.settings.hideImageBorder || false
            })
          }
          
          // 确保项目有必要的属性
          if (!newProject.canvasOffset) {
            newProject.canvasOffset = { x: 0, y: 0 }
          }
          if (!newProject.canvasScale) {
            newProject.canvasScale = 1
          }
          if (!newProject.items) {
            newProject.items = []
          }
          
          // 清除图片扩展名缓存
          imageExtensionIndexMap.current.clear()
          
          // 计算居中偏移量
          const scale = newProject.canvasScale
          const centerOffset = calculateCenterOffset(newProject.items as ShareItem[], scale)
          
          // 更新项目数据
          setProject(newProject)
          setOriginalItems(JSON.parse(JSON.stringify(newProject.items || [])) as ShareItem[])
          
          // 设置居中的画布位置
          setCanvasOffset(centerOffset)
          setCanvasScale(scale)
          
          // 添加到历史记录
          addToHistory(pendingPasswordShareId)
          
          // 更新URL（使用 pushState 创建新的浏览器历史记录）
          window.history.pushState({ shareId: pendingPasswordShareId }, '', `/share/${pendingPasswordShareId}`)
          
          // 更新导航状态
          updateNavigationState()
          
          // 关闭对话框，重置状态
          setShowPasswordDialog(false)
          setPasswordInput('')
          setPendingPasswordShareId(null)
        } else {
          setPasswordError(result.message || '加载失败')
        }
      } catch (err: any) {
        setPasswordError(err.message || '验证失败')
      }
      setIsVerifying(false)
      return
    }
    
    // 原有逻辑：初始加载时的密码验证
    const success = await loadProjectWithPassword(passwordInput)
    
    if (success) {
      setShowPasswordDialog(false)
      setPasswordInput('')
    }
    
    setIsVerifying(false)
  }

  // 加载动画效果控制 - 项目加载成功后开始播放动画
  useEffect(() => {
    if (!showLoadingAnimation) return
    
    // 动画播放完毕后开始淡出（1.8秒动画）
    const animationTimer = setTimeout(() => {
      setLoadingAnimationHidden(true)
    }, 1800)
    
    // 淡出完成后移除加载动画（1.8秒动画 + 0.5秒淡出）
    const removeTimer = setTimeout(() => {
      setShowLoadingAnimation(false)
    }, 2300)
    
    return () => {
      clearTimeout(animationTimer)
      clearTimeout(removeTimer)
    }
  }, [showLoadingAnimation])

  // 座右铭字符动画效果（仅针对特定文本）
  useEffect(() => {
    const canvasMotto = (project as any)?.canvasMotto
    if (!canvasMotto) {
      setMottoDisplayText('')
      setIsMottoAnimating(false)
      return
    }

    // 只有特定文本才使用动画效果
    if (canvasMotto === SPECIAL_MOTTO) {
      setIsMottoAnimating(true)
      setMottoDisplayText('')

      // 清除之前的动画
      if (mottoAnimationRef.current) {
        clearTimeout(mottoAnimationRef.current)
      }

      // 第一次加载或刷新时延长显示时间（每个字符150ms，正常100ms）
      const charDelay = isFirstLoadRef.current ? 150 : 100

      let currentIndex = 0
      const animate = () => {
        if (currentIndex < canvasMotto.length) {
          setMottoDisplayText(canvasMotto.substring(0, currentIndex + 1))
          currentIndex++
          mottoAnimationRef.current = window.setTimeout(animate, charDelay)
        } else {
          setIsMottoAnimating(false)
          // 标记第一次加载完成
          isFirstLoadRef.current = false
        }
      }

      // 延迟开始动画，让页面先渲染
      mottoAnimationRef.current = window.setTimeout(animate, 300)
    } else {
      // 其他座右铭直接显示
      setMottoDisplayText(canvasMotto)
      setIsMottoAnimating(false)
    }

    return () => {
      if (mottoAnimationRef.current) {
        clearTimeout(mottoAnimationRef.current)
      }
    }
  }, [(project as any)?.canvasMotto])

  // 用于跟踪是否已经完成初始居中
  const hasInitialCenteredRef = useRef(false)
  
  // 自动居中功能（只在首次加载时执行）
  useEffect(() => {
    // 如果已经完成初始居中，不再执行
    if (hasInitialCenteredRef.current) return
    if (!project || !contentRef.current || !project.items || project.items.length === 0) return

    // 计算所有items的边界
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity

    project.items.forEach(item => {
      const x = item.x || 0
      const y = item.y || 0
      const width = item.width || 300
      const height = item.height || 200

      minX = Math.min(minX, x)
      minY = Math.min(minY, y)
      maxX = Math.max(maxX, x + width)
      maxY = Math.max(maxY, y + height)
    })

    // 计算画布内容的中心点
    const contentCenterX = (minX + maxX) / 2
    const contentCenterY = (minY + maxY) / 2

    // 计算视口大小
    const viewport = viewportRef.current
    if (!viewport) return

    const viewportWidth = viewport.clientWidth
    const viewportHeight = viewport.clientHeight

    // 计算合适的缩放比例（留出边距）
    const padding = 80
    const scaleX = (viewportWidth - padding * 2) / (maxX - minX || 1)
    const scaleY = (viewportHeight - padding * 2) / (maxY - minY || 1)
    const initialScale = Math.min(scaleX, scaleY, 1) // 不超过1倍

    // 计算偏移量，使内容居中
    const offsetX = viewportWidth / 2 - contentCenterX * initialScale
    const offsetY = viewportHeight / 2 - contentCenterY * initialScale

    setCanvasOffset({ x: offsetX, y: offsetY })
    setCanvasScale(initialScale)
    
    // 标记已完成初始居中
    hasInitialCenteredRef.current = true
  }, [project])

  // 处理画布平移和缩放（鼠标和触摸）
  useEffect(() => {
    if (!viewportRef.current || !project) return

    const viewport = viewportRef.current
    let dragStartPos = { x: 0, y: 0 }
    let isDragging = false
    const CLICK_THRESHOLD = 5 // 移动超过5px才认为是拖拽

    // 触摸相关状态
    let touchStartDistance = 0
    let touchStartScale = 1
    let touchStartCenter = { x: 0, y: 0 }
    let touchStartOffset = { x: 0, y: 0 }
    let lastTouchTime = 0
    let touchStartPos = { x: 0, y: 0 }

    // 计算两点之间的距离
    const getDistance = (touch1: Touch, touch2: Touch) => {
      return Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY)
    }

    // 计算两点的中心点
    const getCenter = (touch1: Touch, touch2: Touch) => {
      return {
        x: (touch1.clientX + touch2.clientX) / 2,
        y: (touch1.clientY + touch2.clientY) / 2
      }
    }

    const handleMouseDown = (e: MouseEvent) => {
      // 只允许左键拖拽，允许在任何地方拖拽（包括元素上）
      if (e.button === 0) {
        // 如果点击的是元素，让元素自己处理（通过onMouseDown）
        const target = e.target as HTMLElement
        if (target.closest('.canvas-item')) {
          // 元素会自己处理，这里不处理
          return
        }
        
        dragStartPos = { x: e.clientX, y: e.clientY }
        isDragging = false
        setIsPanning(true)
        panStartRef.current = {
          x: e.clientX,
          y: e.clientY,
          offsetX: canvasOffset.x,
          offsetY: canvasOffset.y
        }
        viewport.style.cursor = 'grabbing'
      }
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (isPanning) {
        // 检测是否真的在拖拽（移动超过阈值）
        const moveDistance = Math.sqrt(
          Math.pow(e.clientX - dragStartPos.x, 2) + 
          Math.pow(e.clientY - dragStartPos.y, 2)
        )
        if (moveDistance > CLICK_THRESHOLD) {
          isDragging = true
        }
        
        const deltaX = e.clientX - panStartRef.current.x
        const deltaY = e.clientY - panStartRef.current.y
        setCanvasOffset({
          x: panStartRef.current.offsetX + deltaX,
          y: panStartRef.current.offsetY + deltaY
        })
      }
    }

    const handleMouseUp = () => {
      setIsPanning(false)
      viewport.style.cursor = 'grab'
      isDragging = false
    }

    // 处理滚轮缩放
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault()
      
      // 获取鼠标在视口中的位置
      const rect = viewport.getBoundingClientRect()
      const mouseX = e.clientX - rect.left
      const mouseY = e.clientY - rect.top

      // 计算鼠标在世界坐标系中的位置（缩放前）
      const worldX = (mouseX - canvasOffset.x) / canvasScale
      const worldY = (mouseY - canvasOffset.y) / canvasScale

      // 计算新的缩放比例
      const delta = e.deltaY > 0 ? 0.9 : 1.1
      const newScale = Math.max(0.1, Math.min(5, canvasScale * delta))

      // 计算缩放后鼠标应该指向的屏幕位置
      const newScreenX = worldX * newScale + canvasOffset.x
      const newScreenY = worldY * newScale + canvasOffset.y

      // 调整偏移量，使鼠标指向的位置保持不变
      setCanvasOffset({
        x: canvasOffset.x - (newScreenX - mouseX),
        y: canvasOffset.y - (newScreenY - mouseY)
      })
      setCanvasScale(newScale)
    }

    // 触摸事件处理
    const handleTouchStart = (e: TouchEvent) => {
      // 检查是否点击了按钮或其他交互元素
      const target = e.target as HTMLElement
      if (target.closest('button') || target.closest('[role="button"]')) {
        // 按钮自己处理，不阻止默认行为
        return
      }
      
      // 如果触摸的是画布元素，让元素自己处理，不阻止默认行为
      if (target.closest('.canvas-item')) {
        return
      }
      
      e.preventDefault()
      
      if (e.touches.length === 1) {
        // 单指触摸：准备拖拽画布空白区域
        const touch = e.touches[0]
        
        touchStartPos = { x: touch.clientX, y: touch.clientY }
        lastTouchTime = Date.now()
        setIsPanning(true)
        panStartRef.current = {
          x: touch.clientX,
          y: touch.clientY,
          offsetX: canvasOffset.x,
          offsetY: canvasOffset.y
        }
      } else if (e.touches.length === 2) {
        // 双指触摸：准备缩放
        e.preventDefault() // 确保阻止默认的双指缩放行为
        const touch1 = e.touches[0]
        const touch2 = e.touches[1]
        touchStartDistance = getDistance(touch1, touch2)
        touchStartScale = canvasScale
        touchStartCenter = getCenter(touch1, touch2)
        const rect = viewport.getBoundingClientRect()
        touchStartCenter = {
          x: touchStartCenter.x - rect.left,
          y: touchStartCenter.y - rect.top
        }
        touchStartOffset = { ...canvasOffset }
        setIsPanning(false) // 双指缩放时禁用拖拽
      }
    }

    const handleTouchMove = (e: TouchEvent) => {
      // 检查是否点击了按钮或其他交互元素
      const target = e.target as HTMLElement
      if (target.closest('button') || target.closest('[role="button"]')) {
        return
      }
      
      // 如果触摸的是画布元素，让元素自己处理
      if (target.closest('.canvas-item')) {
        return
      }
      
      e.preventDefault()
      
      if (e.touches.length === 1 && isPanning) {
        // 单指移动：拖拽画布
        const touch = e.touches[0]
        const deltaX = touch.clientX - panStartRef.current.x
        const deltaY = touch.clientY - panStartRef.current.y
        setCanvasOffset({
          x: panStartRef.current.offsetX + deltaX,
          y: panStartRef.current.offsetY + deltaY
        })
      } else if (e.touches.length === 2) {
        // 双指移动：缩放画布
        e.preventDefault() // 确保阻止默认的双指缩放行为
        const touch1 = e.touches[0]
        const touch2 = e.touches[1]
        const currentDistance = getDistance(touch1, touch2)
        if (touchStartDistance > 0) {
          const scaleRatio = currentDistance / touchStartDistance
          const newScale = Math.max(0.1, Math.min(5, touchStartScale * scaleRatio))
          
          // 计算当前中心点
          const currentCenter = getCenter(touch1, touch2)
          const rect = viewport.getBoundingClientRect()
          const currentCenterX = currentCenter.x - rect.left
          const currentCenterY = currentCenter.y - rect.top
          
          // 计算世界坐标系中的中心点位置
          const worldX = (touchStartCenter.x - touchStartOffset.x) / touchStartScale
          const worldY = (touchStartCenter.y - touchStartOffset.y) / touchStartScale
          
          // 计算新的偏移量，使中心点保持不变
          setCanvasOffset({
            x: currentCenterX - worldX * newScale,
            y: currentCenterY - worldY * newScale
          })
          setCanvasScale(newScale)
        }
      }
    }

    const handleTouchEnd = (e: TouchEvent) => {
      // 检查是否触摸的是画布元素，让元素自己处理
      const target = e.target as HTMLElement
      if (target.closest('.canvas-item')) {
        return
      }
      
      e.preventDefault()
      
      if (e.touches.length === 0) {
        // 所有手指都离开
        setIsPanning(false)
      } else if (e.touches.length === 1) {
        // 从双指变为单指，准备拖拽
        const touch = e.touches[0]
        setIsPanning(true)
        panStartRef.current = {
          x: touch.clientX,
          y: touch.clientY,
          offsetX: canvasOffset.x,
          offsetY: canvasOffset.y
        }
      }
    }

    // 添加事件监听器
    viewport.addEventListener('mousedown', handleMouseDown)
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    viewport.addEventListener('wheel', handleWheel, { passive: false })
    
    // 触摸事件
    viewport.addEventListener('touchstart', handleTouchStart, { passive: false })
    viewport.addEventListener('touchmove', handleTouchMove, { passive: false })
    viewport.addEventListener('touchend', handleTouchEnd, { passive: false })
    
    viewport.style.cursor = 'grab'
    viewport.style.touchAction = 'none' // 禁用默认触摸行为，允许自定义手势
    viewport.style.webkitUserSelect = 'none' // 禁用文本选择
    viewport.style.userSelect = 'none'

    return () => {
      viewport.removeEventListener('mousedown', handleMouseDown)
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
      viewport.removeEventListener('wheel', handleWheel)
      viewport.removeEventListener('touchstart', handleTouchStart)
      viewport.removeEventListener('touchmove', handleTouchMove)
      viewport.removeEventListener('touchend', handleTouchEnd)
    }
  }, [project, canvasOffset, canvasScale, isPanning])

  // 恢复原始状态
  const handleResetItems = () => {
    if (!project || originalItems.length === 0) return
    
    const resetItems = JSON.parse(JSON.stringify(originalItems)) as ShareItem[]
    setProject({
      ...project,
      items: resetItems as any
    })
    setActiveItemIndex(null)
    setInteractionMode('none')
  }

  // 切换锁定状态
  const handleToggleLock = () => {
    const newLocked = !isLocked
    setIsLocked(newLocked)
    if (newLocked) {
      // 锁定时，禁止交互
      setAllowItemInteraction(false)
      setActiveItemIndex(null)
      setInteractionMode('none')
    } else {
      // 解锁时，允许交互
      setAllowItemInteraction(true)
    }
  }

  // 处理元素点击/触摸的辅助函数
  const handleItemInteraction = (
    e: React.MouseEvent | React.TouchEvent,
    itemIndex: number,
    isWelcomeText: boolean = false
  ) => {
    // 如果允许交互且不是欢迎文本且未锁定，处理元素操作
    if (allowItemInteraction && !isWelcomeText && !isLocked && project) {
      e.stopPropagation()
      e.preventDefault()
      
      const item = project.items?.[itemIndex] as ShareItem
      if (!item) return
      
      const isTouch = 'touches' in e
      const startPos = isTouch 
        ? { x: (e as React.TouchEvent).touches[0].clientX, y: (e as React.TouchEvent).touches[0].clientY }
        : { x: (e as React.MouseEvent).clientX, y: (e as React.MouseEvent).clientY }
      
      // 将元素移动到数组末尾（提升到最上层）
      if (!project.items) return
      
      // 计算最大 zIndex
      const maxZIndex = Math.max(...project.items.map((it: any, idx: number) => it.zIndex ?? idx), 0)
      
      const updatedItems = [...project.items] as ShareItem[]
      const [movedItem] = updatedItems.splice(itemIndex, 1)
      // 更新 zIndex 为最大值 + 1，确保层级真正提升
      movedItem.zIndex = maxZIndex + 1
      updatedItems.push(movedItem)
      const newItemIndex = updatedItems.length - 1
      
      setProject({
        ...project,
        items: updatedItems as any
      })
      
      // 激活元素（使用新的索引）
      setActiveItemIndex(newItemIndex)
      setHoveredItemIndex(null) // 清除悬停状态
      
      // 默认拖动模式
      setInteractionMode('drag')
      interactionStartRef.current = {
        x: startPos.x,
        y: startPos.y,
        itemX: item.x || 0,
        itemY: item.y || 0,
        itemWidth: item.width || 300,
        itemHeight: item.height || 200,
        itemRotation: item.rotation || 0
      }
      
      // 使用新的索引进行后续操作
      const activeIndex = newItemIndex
      
      let currentMode = 'drag' // 使用局部变量跟踪模式
      let hasMoved = false // 跟踪是否发生了移动
      
      const handleMove = (moveEvent: MouseEvent | TouchEvent) => {
        if (!interactionStartRef.current) return
        
        const currentPos = 'touches' in moveEvent && moveEvent.touches.length > 0
          ? { x: moveEvent.touches[0].clientX, y: moveEvent.touches[0].clientY }
          : { x: (moveEvent as MouseEvent).clientX, y: (moveEvent as MouseEvent).clientY }
        
        // 使用屏幕坐标检测是否发生了移动（与 handleUp 保持一致）
        const screenDeltaX = currentPos.x - interactionStartRef.current.x
        const screenDeltaY = currentPos.y - interactionStartRef.current.y
        const screenMoveDistance = Math.sqrt(screenDeltaX * screenDeltaX + screenDeltaY * screenDeltaY)
        if (screenMoveDistance > 5) {
          hasMoved = true
        }
        
        // 使用缩放后的距离来计算实际的元素移动
        const deltaX = screenDeltaX / canvasScale
        const deltaY = screenDeltaY / canvasScale
        
        // 使用函数式更新来获取最新的 project 状态
        setProject(prevProject => {
          if (!prevProject || !prevProject.items) return prevProject
          const updatedItems = [...prevProject.items]
          const currentItem = updatedItems[activeIndex]
          
          if (currentMode === 'drag' && hasMoved && currentItem) {
            // 拖动
            currentItem.x = interactionStartRef.current!.itemX + deltaX
            currentItem.y = interactionStartRef.current!.itemY + deltaY
          }
          
          return {
            ...prevProject,
            items: updatedItems
          } as Project
        })
      }
      
      const handleUp = (upEvent?: MouseEvent | TouchEvent) => {
        if (isTouch) {
          window.removeEventListener('touchmove', handleMove as EventListener)
          window.removeEventListener('touchend', handleUp)
        } else {
          window.removeEventListener('mousemove', handleMove as EventListener)
          window.removeEventListener('mouseup', handleUp)
        }
        
        // 如果点击了控制按钮（旋转、缩放等），不打开查看器
        if (controlButtonClickedRef.current) {
          controlButtonClickedRef.current = false
          setActiveItemIndex(activeIndex)
          interactionStartRef.current = null
          return
        }
        
        // 检查是否只是点击（没有拖动）
        if (interactionStartRef.current && upEvent) {
          const endPos = 'touches' in upEvent && upEvent.touches.length > 0
            ? { x: upEvent.touches[0].clientX, y: upEvent.touches[0].clientY }
            : 'changedTouches' in upEvent && upEvent.changedTouches.length > 0
            ? { x: upEvent.changedTouches[0].clientX, y: upEvent.changedTouches[0].clientY }
            : { x: (upEvent as MouseEvent).clientX, y: (upEvent as MouseEvent).clientY }
          
          const moveDistance = Math.sqrt(
            Math.pow(endPos.x - interactionStartRef.current.x, 2) + 
            Math.pow(endPos.y - interactionStartRef.current.y, 2)
          )
          
          if (moveDistance < 5 && !hasMoved) {
            // 只是点击，打开查看器
            handleOpenViewer(activeIndex)
            setActiveItemIndex(null)
            setInteractionMode('none')
          } else if (hasMoved) {
            // 拖动完成，保持激活状态以便继续操作
            setActiveItemIndex(activeIndex)
            setInteractionMode('none')
          } else {
            // 激活元素但不拖动
            setActiveItemIndex(activeIndex)
          }
        } else {
          // 没有upEvent，保持激活状态
          setActiveItemIndex(activeIndex)
        }
        interactionStartRef.current = null
        hasMoved = false
      }
      
      if (isTouch) {
        window.addEventListener('touchmove', handleMove as EventListener, { passive: false })
        window.addEventListener('touchend', handleUp, { passive: false })
      } else {
        window.addEventListener('mousemove', handleMove as EventListener)
        window.addEventListener('mouseup', handleUp)
      }
      
      return
    }
    
    // 原有逻辑：画布平移或打开查看器
    e.stopPropagation()
    e.preventDefault()
    
    const isTouch = 'touches' in e
    const startPos = isTouch 
      ? { x: (e as React.TouchEvent).touches[0].clientX, y: (e as React.TouchEvent).touches[0].clientY }
      : { x: (e as React.MouseEvent).clientX, y: (e as React.MouseEvent).clientY }
    
    let isDragging = false
    let hasStartedPanning = false
    const CLICK_THRESHOLD = 5
    
    const handleMove = (moveEvent: MouseEvent | TouchEvent) => {
      const currentPos = 'touches' in moveEvent && moveEvent.touches.length > 0
        ? { x: moveEvent.touches[0].clientX, y: moveEvent.touches[0].clientY }
        : { x: (moveEvent as MouseEvent).clientX, y: (moveEvent as MouseEvent).clientY }
      
      const moveDistance = Math.sqrt(
        Math.pow(currentPos.x - startPos.x, 2) + 
        Math.pow(currentPos.y - startPos.y, 2)
      )
      
      if (moveDistance > CLICK_THRESHOLD && !hasStartedPanning) {
        isDragging = true
        hasStartedPanning = true
        setIsPanning(true)
        panStartRef.current = {
          x: currentPos.x,
          y: currentPos.y,
          offsetX: canvasOffset.x,
          offsetY: canvasOffset.y
        }
        if (viewportRef.current) {
          viewportRef.current.style.cursor = 'grabbing'
        }
      }
      
      if (hasStartedPanning) {
        const deltaX = currentPos.x - panStartRef.current.x
        const deltaY = currentPos.y - panStartRef.current.y
        setCanvasOffset({
          x: panStartRef.current.offsetX + deltaX,
          y: panStartRef.current.offsetY + deltaY
        })
      }
    }
    
    const handleUp = () => {
      if (isTouch) {
        window.removeEventListener('touchmove', handleMove as EventListener)
        window.removeEventListener('touchend', handleUp)
      } else {
        window.removeEventListener('mousemove', handleMove as EventListener)
        window.removeEventListener('mouseup', handleUp)
      }
      
      if (!isDragging && !isWelcomeText) {
        handleOpenViewer(itemIndex)
      }
      
      if (hasStartedPanning) {
        setIsPanning(false)
        if (viewportRef.current) {
          viewportRef.current.style.cursor = 'grab'
        }
      }
    }
    
    if (isTouch) {
      window.addEventListener('touchmove', handleMove as EventListener, { passive: false })
      window.addEventListener('touchend', handleUp, { passive: false })
    } else {
      window.addEventListener('mousemove', handleMove as EventListener)
      window.addEventListener('mouseup', handleUp)
    }
  }

  // 处理旋转
  const handleRotateItem = (itemIndex: number, delta: number) => {
    if (!project || !project.items) return
    
    const updatedItems = [...project.items] as ShareItem[]
    const item = updatedItems[itemIndex] as ShareItem
    if (!item) return
    
    const currentRotation = item.rotation || 0
    item.rotation = (currentRotation + delta) % 360
    if (item.rotation < 0) item.rotation += 360
    
    setProject({
      ...project,
      items: updatedItems as any
    })
  }

  // 处理缩放开始
  const handleResizeStart = (e: React.MouseEvent | React.TouchEvent, itemIndex: number) => {
    e.stopPropagation()
    e.preventDefault()
    
    if (!project || !project.items) return
    
    const item = project.items[itemIndex] as ShareItem
    if (!item) return
    
    setInteractionMode('resize')
    setActiveItemIndex(itemIndex)
    
    const startPos = 'touches' in e && e.touches.length > 0
      ? { x: e.touches[0].clientX, y: e.touches[0].clientY }
      : { x: (e as React.MouseEvent).clientX, y: (e as React.MouseEvent).clientY }
    
    interactionStartRef.current = {
      x: startPos.x,
      y: startPos.y,
      itemX: item.x || 0,
      itemY: item.y || 0,
      itemWidth: item.width || 300,
      itemHeight: item.height || 200,
      itemRotation: item.rotation || 0
    }
    
    const handleMove = (moveEvent: MouseEvent | TouchEvent) => {
      if (!interactionStartRef.current) return
      
      const currentPos = 'touches' in moveEvent && moveEvent.touches.length > 0
        ? { x: moveEvent.touches[0].clientX, y: moveEvent.touches[0].clientY }
        : { x: (moveEvent as MouseEvent).clientX, y: (moveEvent as MouseEvent).clientY }
      
      const deltaX = (currentPos.x - interactionStartRef.current.x) / canvasScale
      const deltaY = (currentPos.y - interactionStartRef.current.y) / canvasScale
      
      // 计算缩放比例 - 根据鼠标移动方向判断放大或缩小
      // 向右下移动放大，向左上移动缩小
      const scaleFactor = (deltaX + deltaY) / 200 // 使用平均值，除以200使缩放更平滑
      const scale = 1 + scaleFactor
      
      const newWidth = Math.max(50, Math.min(2000, interactionStartRef.current.itemWidth * scale))
      const newHeight = Math.max(50, Math.min(2000, interactionStartRef.current.itemHeight * scale))
      
      // 使用函数式更新来获取最新的 project 状态
      setProject(prevProject => {
        if (!prevProject || !prevProject.items) return prevProject
        const updatedItems = [...prevProject.items]
        const currentItem = updatedItems[itemIndex]
        if (currentItem) {
          currentItem.width = newWidth
          currentItem.height = newHeight
        }
        return {
          ...prevProject,
          items: updatedItems
        } as Project
      })
    }
    
    const handleUp = () => {
      window.removeEventListener('mousemove', handleMove as EventListener)
      window.removeEventListener('mouseup', handleUp)
      window.removeEventListener('touchmove', handleMove as EventListener)
      window.removeEventListener('touchend', handleUp)
      setInteractionMode('none')
      controlButtonClickedRef.current = false
    }
    
    window.addEventListener('mousemove', handleMove as EventListener)
    window.addEventListener('mouseup', handleUp)
    window.addEventListener('touchmove', handleMove as EventListener, { passive: false })
    window.addEventListener('touchend', handleUp, { passive: false })
  }

  // 渲染画布项目
  const renderCanvasItem = (item: ShareItem, index: number) => {
    const isActive = allowItemInteraction && !isLocked && activeItemIndex === index
    const isHovered = allowItemInteraction && !isLocked && hoveredItemIndex === index && !isActive
    const isWelcomeText = item.isWelcomeText || false
    const itemStyle: React.CSSProperties = {
      position: 'absolute',
      left: item.x || 0,
      top: item.y || 0,
      width: item.width || 300,
      height: item.height || 200,
      pointerEvents: 'auto',
      userSelect: 'none',
      // 使用 zIndex 控制层级，保持设计时的层级顺序
      zIndex: item.zIndex ?? index,
      transform: item.rotation ? `rotate(${item.rotation}deg)` : undefined,
      transformOrigin: 'center center',
      touchAction: 'none', // 禁用浏览器默认触摸行为，确保自定义触摸处理生效
      transition: 'outline 0.15s ease, outline-offset 0.15s ease'
    }
    
    // 交互模式下的样式
    if (allowItemInteraction && !isLocked && !isWelcomeText) {
      itemStyle.cursor = isActive ? 'move' : 'grab'
      if (isActive) {
        // 激活状态：蓝色虚线边框
        itemStyle.outline = '2px dashed #3b82f6'
        itemStyle.outlineOffset = '2px'
      } else if (isHovered) {
        // 悬停状态：蓝色实线边框
        itemStyle.outline = '2px solid #3b82f6'
        itemStyle.outlineOffset = '2px'
      }
    }

    if (item.type === 'text') {
      // 根据设置应用样式
      const isWelcomeText = item.isWelcomeText || false
      const isMottoStyle = settings.mottoStyle && !isWelcomeText
      const showBorder = settings.showBorder && !isMottoStyle
      const hasShareLink = !!item.shareLink
      
      // 容器样式
      const containerClassName = isWelcomeText || isMottoStyle
        ? 'canvas-item'
        : showBorder
        ? 'canvas-item rounded-md p-3 bg-white shadow-md border border-gray-200'
        : 'canvas-item rounded-md p-3 bg-white no-border'
      
      const containerStyle: React.CSSProperties = {
        ...itemStyle,
        minHeight: 'auto',
        height: item.height ? `${item.height}px` : 'auto',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'visible' // 改为visible以显示光晕效果
      }
      
      // 座右铭风格或欢迎文本：完全透明
      if (isWelcomeText || isMottoStyle) {
        containerStyle.background = 'transparent'
        containerStyle.border = 'none'
        containerStyle.boxShadow = 'none'
        containerStyle.padding = '0'
      }
      
      // 有分享链接的文本框添加光晕效果
      if (hasShareLink && !isWelcomeText) {
        containerStyle.boxShadow = '0 0 20px 8px rgba(6, 182, 212, 0.5), 0 0 40px 16px rgba(6, 182, 212, 0.3), 0 0 60px 24px rgba(6, 182, 212, 0.1)'
        containerStyle.animation = 'sharelink-glow 2s ease-in-out infinite alternate'
      }
      
      // 检测是否是富文本内容
      const isRichText = item.content && /<(h[1-6]|p|ul|ol|li|a|b|i|u|strong|em|strike|s|br|div|span)[^>]*>/i.test(item.content)
      
      // 文本内容样式
      const contentStyle: React.CSSProperties = {
        pointerEvents: 'auto',
        whiteSpace: isRichText ? 'normal' : 'pre-wrap', // 富文本用 normal，纯文本用 pre-wrap
        wordWrap: 'break-word',
        fontSize: `${item.fontSize || settings.fontSize || 14}px`,
        fontFamily: item.fontFamily || settings.fontFamily || 'Microsoft YaHei, 微软雅黑, Arial, sans-serif',
        color: '#1f2937'
      }
      
      if (isWelcomeText) {
        // 欢迎文本样式
        contentStyle.fontSize = '18px'
        contentStyle.fontFamily = 'STZhongsong, 华文中宋, SimSun, serif'
        contentStyle.background = 'transparent'
        contentStyle.border = 'none'
        contentStyle.padding = '0'
        contentStyle.margin = '0'
        contentStyle.cursor = 'default'
      } else if (isMottoStyle) {
        // 座右铭风格：完全透明
        contentStyle.border = 'none'
        contentStyle.backgroundColor = 'transparent'
        contentStyle.boxShadow = 'none'
        contentStyle.padding = '0'
      } else if (!showBorder) {
        // 扁平化显示：无边框
        contentStyle.border = 'none'
        contentStyle.backgroundColor = 'transparent'
        contentStyle.boxShadow = 'none'
      } else {
        // 默认样式
        contentStyle.minHeight = '60px'
        contentStyle.maxHeight = '600px'
        contentStyle.overflowY = 'auto'
      }
      
      return (
        <div
          key={item.uuid || index}
          className={containerClassName}
          style={containerStyle}
          onMouseDown={(e) => {
            if (e.button === 0) {
              // 检查点击目标是否是链接icon，如果是则跳过处理
              const target = e.target as HTMLElement
              if (target.closest('[data-share-link-icon]')) {
                return
              }
              // 如果允许交互且未锁定，让handleItemInteraction处理
              if (allowItemInteraction && !isLocked) {
                handleItemInteraction(e, index, isWelcomeText)
              } else if (!isLocked && !isWelcomeText) {
                // 未锁定但未启用交互模式：文本元素特殊处理 - 支持层级提升和缩放
                e.stopPropagation()
                e.preventDefault()
                
                if (!project || !project.items) return
                
                const item = project.items[index] as ShareItem
                if (!item) return
                
                // 计算最大 zIndex
                const maxZIndex = Math.max(...project.items.map((it: any, idx: number) => it.zIndex ?? idx), 0)
                
                // 将文本元素移动到数组末尾（提升到最上层）
                const updatedItems = [...project.items] as ShareItem[]
                const [movedItem] = updatedItems.splice(index, 1)
                // 更新 zIndex 为最大值 + 1
                movedItem.zIndex = maxZIndex + 1
                updatedItems.push(movedItem)
                const newItemIndex = updatedItems.length - 1
                
                setProject({
                  ...project,
                  items: updatedItems as any
                })
                
                // 激活元素（使用新的索引）
                setActiveItemIndex(newItemIndex)
                setHoveredItemIndex(null)
              }
            }
          }}
          onTouchStart={(e) => {
            if (e.touches.length === 1) {
              // 检查点击目标是否是链接icon，如果是则跳过处理
              const target = e.target as HTMLElement
              if (target.closest('[data-share-link-icon]')) {
                return
              }
              // 阻止事件冒泡到 viewport，由元素自己处理
              e.stopPropagation()
              
              // 如果允许交互且未锁定，让handleItemInteraction处理
              if (allowItemInteraction && !isLocked) {
                handleItemInteraction(e, index, isWelcomeText)
                return
              }
              
              // 未锁定但未启用交互模式：文本元素特殊处理 - 支持层级提升和缩放
              if (!isLocked && !isWelcomeText) {
                e.preventDefault()
                
                if (!project || !project.items) return
                
                const item = project.items[index] as ShareItem
                if (!item) return
                
                // 计算最大 zIndex
                const maxZIndex = Math.max(...project.items.map((it: any, idx: number) => it.zIndex ?? idx), 0)
                
                // 将文本元素移动到数组末尾（提升到最上层）
                const updatedItems = [...project.items] as ShareItem[]
                const [movedItem] = updatedItems.splice(index, 1)
                // 更新 zIndex 为最大值 + 1
                movedItem.zIndex = maxZIndex + 1
                updatedItems.push(movedItem)
                const newItemIndex = updatedItems.length - 1
                
                setProject({
                  ...project,
                  items: updatedItems as any
                })
                
                // 激活元素（使用新的索引）
                setActiveItemIndex(newItemIndex)
                setHoveredItemIndex(null)
              }
            }
          }}
          onMouseEnter={() => {
            // 文本元素：只要未锁定且不是欢迎文本就允许悬停状态
            if (!isLocked && !isWelcomeText) {
              setHoveredItemIndex(index)
            }
          }}
          onMouseLeave={() => {
            setHoveredItemIndex(null)
          }}
        >
          <div
            className="text-content"
            dangerouslySetInnerHTML={{ __html: item.content || '' }}
            style={contentStyle}
          />
          {/* 有分享链接的文本框显示链接图标 */}
          {hasShareLink && !isWelcomeText && (
            <div
              data-share-link-icon="true"
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                width: 32,
                height: 32,
                backgroundColor: 'rgba(6, 182, 212, 0.9)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                animation: 'sharelink-icon-pulse 2s ease-in-out infinite',
                cursor: 'pointer',
                zIndex: 1000,
                pointerEvents: 'auto'
              }}
              title="点击跳转到链接的画布"
              onMouseDown={(e) => {
                e.stopPropagation()
              }}
              onMouseUp={(e) => {
                e.stopPropagation()
                e.preventDefault()
                if (item.shareLink) {
                  handleShareLinkClick(item.shareLink, e.currentTarget)
                }
              }}
              onTouchStart={(e) => {
                e.stopPropagation()
              }}
              onTouchEnd={(e) => {
                e.stopPropagation()
                e.preventDefault()
                if (item.shareLink) {
                  handleShareLinkClick(item.shareLink, e.currentTarget)
                }
              }}
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                if (item.shareLink) {
                  handleShareLinkClick(item.shareLink, e.currentTarget)
                }
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
            </div>
          )}
          {/* 交互控制UI - 文本元素：只要未锁定且不是欢迎文本且激活或悬停就显示缩放控制点 */}
          {!isLocked && !isWelcomeText && (isActive || isHovered || activeItemIndex === index || hoveredItemIndex === index) && (
            <>
              {/* 缩放控制点 */}
              <div
                style={{
                  position: 'absolute',
                  right: -8,
                  top: -8,
                  width: 16,
                  height: 16,
                  backgroundColor: '#3b82f6',
                  border: '2px solid white',
                  borderRadius: '50%',
                  cursor: 'nwse-resize',
                  zIndex: 1000
                }}
                onMouseDown={(e) => {
                  controlButtonClickedRef.current = true
                  handleResizeStart(e, index)
                }}
                onTouchStart={(e) => {
                  controlButtonClickedRef.current = true
                  handleResizeStart(e, index)
                }}
              />
              {/* 旋转按钮 - 仅在启用交互模式时显示 */}
              {allowItemInteraction && (
                <div
                  style={{
                    position: 'absolute',
                    right: -8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 24,
                    height: 24,
                    backgroundColor: '#3b82f6',
                    border: '2px solid white',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation()
                    controlButtonClickedRef.current = true
                  }}
                  onClick={(e) => {
                    e.stopPropagation()
                    controlButtonClickedRef.current = true
                    handleRotateItem(index, 90)
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
                  </svg>
                </div>
              )}
            </>
          )}
        </div>
      )
    } else if (item.type === 'image') {
      // 构建图片URL - 支持 OSS 和本地存储
      // 优先使用thumbnailFileId（压缩图）用于画布显示，点击时使用fileId（原图）打开查看器
      // 统一使用fileId，兼容旧数据的imageId
      const fileId = item.fileId || item.imageId
      const thumbnailFileId = (item as any).thumbnailFileId // 压缩图fileId
      
      // 优先使用压缩图，如果没有则使用原图
      const displayFileId = thumbnailFileId || fileId
      const hasExtension = displayFileId && displayFileId.includes('.')
      
      // 获取文件扩展名：优先使用 item.ext，其次从 fileType 提取，最后默认 png
      const itemExt = item.ext || item.fileType?.split('/')[1] || 'png'
      // 处理 jpeg -> jpg 的映射
      const fileExtension = itemExt === 'jpeg' ? 'jpg' : itemExt
      
      // 如果 fileId 没有扩展名，使用 item 中保存的扩展名
      let imageUrl = ''
      if (displayFileId && shareInfo?.userUuid && project?.uuid) {
        if (hasExtension) {
          // 已经有扩展名，直接使用
          imageUrl = getFileUrl(displayFileId, shareInfo.userUuid, project.uuid)
        } else {
          // 没有扩展名，使用 item 中保存的扩展名
          imageUrl = getFileUrl(displayFileId, shareInfo.userUuid, project.uuid, fileExtension)
        }
      } else if (item.imageSrc) {
        imageUrl = item.imageSrc
      }
      
      // fileId 用于点击时打开查看器（通过 handleItemClick 使用）
      void fileId

      // 根据设置应用图片边框样式
      const hideImageBorder = settings.hideImageBorder || false
      const hasShareLink = !!item.shareLink
      
      const containerStyle: React.CSSProperties = {
        ...itemStyle,
        overflow: 'visible', // 改为visible以显示光晕效果
        padding: hideImageBorder ? '0' : '12px',
        touchAction: 'none' // 禁用浏览器默认触摸行为，确保自定义触摸处理生效
      }
      
      if (hideImageBorder) {
        containerStyle.background = 'transparent'
        containerStyle.border = 'none'
        containerStyle.boxShadow = 'none'
      }
      
      // 有分享链接的图片添加光晕效果
      if (hasShareLink) {
        containerStyle.boxShadow = '0 0 20px 8px rgba(6, 182, 212, 0.5), 0 0 40px 16px rgba(6, 182, 212, 0.3), 0 0 60px 24px rgba(6, 182, 212, 0.1)'
        containerStyle.animation = 'sharelink-glow 2s ease-in-out infinite alternate'
      }

      const imgStyle: React.CSSProperties = {
        width: '100%',
        height: '100%',
        objectFit: item.containerFitMode === 'cover' ? 'cover' : 'contain',
        borderRadius: '4px',
        border: hideImageBorder ? 'none' : '1px solid #e5e7eb',
        display: 'block'
      }


      return (
        <div
          key={item.uuid || index}
          className="canvas-item rounded-md bg-white shadow-md border border-gray-200"
          style={containerStyle}
          onMouseDown={(e) => {
            if (e.button === 0) {
              // 检查点击目标是否是链接icon，如果是则跳过处理
              const target = e.target as HTMLElement
              if (target.closest('[data-share-link-icon]')) {
                return
              }
              
              // 如果允许交互且未锁定，让handleItemInteraction处理
              if (allowItemInteraction && !isLocked) {
                handleItemInteraction(e, index, false)
              } else if (!isLocked) {
                // 未锁定但未启用交互模式：图片元素特殊处理 - 支持层级提升和缩放
                e.stopPropagation()
                e.preventDefault()
                
                if (!project || !project.items) return
                
                const item = project.items[index] as ShareItem
                if (!item) return
                
                // 计算最大 zIndex
                const maxZIndex = Math.max(...project.items.map((it: any, idx: number) => it.zIndex ?? idx), 0)
                
                // 将图片元素移动到数组末尾（提升到最上层）
                const updatedItems = [...project.items] as ShareItem[]
                const [movedItem] = updatedItems.splice(index, 1)
                // 更新 zIndex 为最大值 + 1
                movedItem.zIndex = maxZIndex + 1
                updatedItems.push(movedItem)
                const newItemIndex = updatedItems.length - 1
                
                setProject({
                  ...project,
                  items: updatedItems as any
                })
                
                // 激活元素（使用新的索引）
                setActiveItemIndex(newItemIndex)
                setHoveredItemIndex(null)
              } else {
                // 锁定模式下：支持点击打开查看器，也支持拖动画布
                e.stopPropagation()
                
                const mouseStartPos = { x: e.clientX, y: e.clientY }
                let hasMoved = false
                let hasStartedPanning = false
                
                const handleMouseMove = (moveEvent: MouseEvent) => {
                  const moveDistance = Math.sqrt(
                    Math.pow(moveEvent.clientX - mouseStartPos.x, 2) +
                    Math.pow(moveEvent.clientY - mouseStartPos.y, 2)
                  )
                  if (moveDistance > 5) {
                    hasMoved = true
                    
                    // 开始画布拖动
                    if (!hasStartedPanning) {
                      hasStartedPanning = true
                      setIsPanning(true)
                      panStartRef.current = {
                        x: moveEvent.clientX,
                        y: moveEvent.clientY,
                        offsetX: canvasOffset.x,
                        offsetY: canvasOffset.y
                      }
                      if (viewportRef.current) {
                        viewportRef.current.style.cursor = 'grabbing'
                      }
                    }
                    
                    // 更新画布偏移
                    if (hasStartedPanning) {
                      const deltaX = moveEvent.clientX - panStartRef.current.x
                      const deltaY = moveEvent.clientY - panStartRef.current.y
                      setCanvasOffset({
                        x: panStartRef.current.offsetX + deltaX,
                        y: panStartRef.current.offsetY + deltaY
                      })
                    }
                  }
                }
                
                const handleMouseUp = (upEvent: MouseEvent) => {
                  const moveDistance = Math.sqrt(
                    Math.pow(upEvent.clientX - mouseStartPos.x, 2) +
                    Math.pow(upEvent.clientY - mouseStartPos.y, 2)
                  )
                  
                  // 如果移动距离小，认为是点击
                  if (moveDistance < 5 && !hasMoved) {
                    // 点击图片始终打开查看器
                    handleOpenViewer(index)
                  }
                  
                  // 结束画布拖动
                  if (hasStartedPanning) {
                    setIsPanning(false)
                    if (viewportRef.current) {
                      viewportRef.current.style.cursor = 'grab'
                    }
                  }
                  
                  window.removeEventListener('mousemove', handleMouseMove)
                  window.removeEventListener('mouseup', handleMouseUp)
                }
                
                window.addEventListener('mousemove', handleMouseMove)
                window.addEventListener('mouseup', handleMouseUp)
              }
            }
          }}
          onTouchStart={(e) => {
            if (e.touches.length === 1) {
              // 检查点击目标是否是链接icon，如果是则跳过处理
              const target = e.target as HTMLElement
              if (target.closest('[data-share-link-icon]')) {
                return
              }
              
              // 阻止事件冒泡到 viewport，由元素自己处理
              e.stopPropagation()
              
              // 如果允许交互且未锁定，让handleItemInteraction处理
              if (allowItemInteraction && !isLocked) {
                handleItemInteraction(e, index, false)
                return
              }
              
              // 未锁定但未启用交互模式：图片元素特殊处理 - 支持层级提升和缩放
              if (!isLocked) {
                e.preventDefault()
                
                if (!project || !project.items) return
                
                const item = project.items[index] as ShareItem
                if (!item) return
                
                // 计算最大 zIndex
                const maxZIndex = Math.max(...project.items.map((it: any, idx: number) => it.zIndex ?? idx), 0)
                
                // 将图片元素移动到数组末尾（提升到最上层）
                const updatedItems = [...project.items] as ShareItem[]
                const [movedItem] = updatedItems.splice(index, 1)
                // 更新 zIndex 为最大值 + 1
                movedItem.zIndex = maxZIndex + 1
                updatedItems.push(movedItem)
                const newItemIndex = updatedItems.length - 1
                
                setProject({
                  ...project,
                  items: updatedItems as any
                })
                
                // 激活元素（使用新的索引）
                setActiveItemIndex(newItemIndex)
                setHoveredItemIndex(null)
                return
              }
              
              // 锁定模式下：支持点击打开查看器，也支持拖动画布
              const touch = e.touches[0]
              const touchStartTime = Date.now()
              const touchStartPos = { x: touch.clientX, y: touch.clientY }
              let hasMoved = false
              let hasStartedPanning = false
              
              const handleTouchMove = (moveEvent: TouchEvent) => {
                if (moveEvent.touches.length > 0) {
                  const moveTouch = moveEvent.touches[0]
                  const moveDistance = Math.sqrt(
                    Math.pow(moveTouch.clientX - touchStartPos.x, 2) +
                    Math.pow(moveTouch.clientY - touchStartPos.y, 2)
                  )
                  if (moveDistance > 5) {
                    hasMoved = true
                    
                    // 开始画布拖动
                    if (!hasStartedPanning) {
                      hasStartedPanning = true
                      setIsPanning(true)
                      panStartRef.current = {
                        x: moveTouch.clientX,
                        y: moveTouch.clientY,
                        offsetX: canvasOffset.x,
                        offsetY: canvasOffset.y
                      }
                    }
                    
                    // 更新画布偏移
                    if (hasStartedPanning) {
                      const deltaX = moveTouch.clientX - panStartRef.current.x
                      const deltaY = moveTouch.clientY - panStartRef.current.y
                      setCanvasOffset({
                        x: panStartRef.current.offsetX + deltaX,
                        y: panStartRef.current.offsetY + deltaY
                      })
                    }
                  }
                }
              }
              
              const handleTouchEnd = (endEvent: TouchEvent) => {
                const endTime = Date.now()
                const timeDiff = endTime - touchStartTime
                
                // 先移除监听器
                window.removeEventListener('touchmove', handleTouchMove)
                window.removeEventListener('touchend', handleTouchEnd)
                
                // 结束画布拖动
                if (hasStartedPanning) {
                  setIsPanning(false)
                }
                
                if (endEvent.changedTouches.length > 0) {
                  const endTouch = endEvent.changedTouches[0]
                  const moveDistance = Math.sqrt(
                    Math.pow(endTouch.clientX - touchStartPos.x, 2) +
                    Math.pow(endTouch.clientY - touchStartPos.y, 2)
                  )
                  
                  // 如果时间短且移动距离小，认为是点击
                  // 使用 setTimeout 确保在 React 状态更新后执行
                  if (timeDiff < 500 && moveDistance < 15 && !hasMoved) {
                    setTimeout(() => {
                      // 点击图片始终打开查看器
                      handleOpenViewer(index)
                    }, 0)
                  }
                }
              }
              
              window.addEventListener('touchmove', handleTouchMove, { passive: true })
              window.addEventListener('touchend', handleTouchEnd)
            }
          }}
          onMouseEnter={() => {
            // 图片元素：只要未锁定就允许悬停状态
            if (!isLocked) {
              setHoveredItemIndex(index)
            }
          }}
          onMouseLeave={() => {
            setHoveredItemIndex(null)
          }}
        >
          <img
            src={imageUrl}
            alt=""
            style={imgStyle}
            loading="eager"
            draggable={false}
            crossOrigin="anonymous"
            onLoad={() => {
              // 图片加载成功
              console.log('[ShareView] 图片加载成功:', imageUrl)
            }}
            onError={(e) => {
              const target = e.target as HTMLImageElement
              console.error('[ShareView] 图片加载失败:', imageUrl)
              
              // 显示占位符
              target.style.display = 'none'
              const placeholder = document.createElement('div')
              placeholder.textContent = '图片加载失败'
              placeholder.style.cssText = 'display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; color: #9ca3af;'
              target.parentElement?.appendChild(placeholder)
            }}
          />
          {/* 有分享链接的图片显示链接图标 */}
          {hasShareLink && (
            <div
              data-share-link-icon="true"
              style={{
                position: 'absolute',
                top: hideImageBorder ? 8 : 20,
                right: hideImageBorder ? 8 : 20,
                width: 32,
                height: 32,
                backgroundColor: 'rgba(6, 182, 212, 0.9)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                animation: 'sharelink-icon-pulse 2s ease-in-out infinite',
                cursor: 'pointer',
                zIndex: 1000,
                pointerEvents: 'auto'
              }}
              title="点击跳转到链接的画布"
              onMouseDown={(e) => {
                e.stopPropagation()
                // 不阻止默认行为，确保 onMouseUp 能正常触发
              }}
              onMouseUp={(e) => {
                e.stopPropagation()
                e.preventDefault()
                if (item.shareLink) {
                  handleShareLinkClick(item.shareLink, e.currentTarget)
                }
              }}
              onTouchStart={(e) => {
                e.stopPropagation()
              }}
              onTouchEnd={(e) => {
                e.stopPropagation()
                e.preventDefault()
                if (item.shareLink) {
                  handleShareLinkClick(item.shareLink, e.currentTarget)
                }
              }}
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                // onClick 作为备用，主要使用 onMouseUp
                if (item.shareLink) {
                  handleShareLinkClick(item.shareLink, e.currentTarget)
                }
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
            </div>
          )}
          {/* 交互控制UI - 图片元素：只要未锁定且激活或悬停就显示缩放控制点 */}
          {!isLocked && (isActive || isHovered || activeItemIndex === index || hoveredItemIndex === index) && (
            <>
              {/* 缩放控制点 */}
              <div
                style={{
                  position: 'absolute',
                  right: -8,
                  top: -8,
                  width: 16,
                  height: 16,
                  backgroundColor: '#3b82f6',
                  border: '2px solid white',
                  borderRadius: '50%',
                  cursor: 'nwse-resize',
                  zIndex: 1000
                }}
                onMouseDown={(e) => {
                  controlButtonClickedRef.current = true
                  handleResizeStart(e, index)
                }}
                onTouchStart={(e) => {
                  controlButtonClickedRef.current = true
                  handleResizeStart(e, index)
                }}
              />
              {/* 旋转按钮 - 仅在启用交互模式时显示 */}
              {allowItemInteraction && (
                <div
                  style={{
                    position: 'absolute',
                    right: -8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 24,
                    height: 24,
                    backgroundColor: '#3b82f6',
                    border: '2px solid white',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation()
                    controlButtonClickedRef.current = true
                  }}
                  onClick={(e) => {
                    e.stopPropagation()
                    controlButtonClickedRef.current = true
                    handleRotateItem(index, 90)
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
                  </svg>
                </div>
              )}
            </>
          )}
        </div>
      )
    } else if (item.type === 'file') {
      // 构建文件URL - 支持 OSS 和本地存储
      // fileUrl 通过 getFileUrl 获取（在渲染时使用）
      void (item.fileId && shareInfo?.userUuid && project?.uuid)
      const hasShareLink = !!item.shareLink

      // 文件容器样式
      const fileContainerStyle: React.CSSProperties = {
        ...itemStyle,
        overflow: 'visible' // 改为visible以显示光晕效果
      }
      
      // 有分享链接的文件添加光晕效果
      if (hasShareLink) {
        fileContainerStyle.boxShadow = '0 0 20px 8px rgba(6, 182, 212, 0.5), 0 0 40px 16px rgba(6, 182, 212, 0.3), 0 0 60px 24px rgba(6, 182, 212, 0.1)'
        fileContainerStyle.animation = 'sharelink-glow 2s ease-in-out infinite alternate'
      }

      return (
        <div
          key={item.uuid || index}
          className="canvas-item rounded-md p-3 bg-white shadow-md border border-gray-200"
          style={fileContainerStyle}
          onMouseDown={(e) => {
            if (e.button === 0) {
              // 检查点击目标是否是链接icon，如果是则跳过处理
              const target = e.target as HTMLElement
              if (target.closest('[data-share-link-icon]')) {
                return
              }
              // 如果允许交互且未锁定，让handleItemInteraction处理
              if (allowItemInteraction && !isLocked) {
                handleItemInteraction(e, index, false)
              } else if (!isLocked) {
                // 未锁定但未启用交互模式：文件元素特殊处理 - 支持层级提升和缩放
                e.stopPropagation()
                e.preventDefault()
                
                if (!project || !project.items) return
                
                const item = project.items[index] as ShareItem
                if (!item) return
                
                // 计算最大 zIndex
                const maxZIndex = Math.max(...project.items.map((it: any, idx: number) => it.zIndex ?? idx), 0)
                
                // 将文件元素移动到数组末尾（提升到最上层）
                const updatedItems = [...project.items] as ShareItem[]
                const [movedItem] = updatedItems.splice(index, 1)
                // 更新 zIndex 为最大值 + 1
                movedItem.zIndex = maxZIndex + 1
                updatedItems.push(movedItem)
                const newItemIndex = updatedItems.length - 1
                
                setProject({
                  ...project,
                  items: updatedItems as any
                })
                
                // 激活元素（使用新的索引）
                setActiveItemIndex(newItemIndex)
                setHoveredItemIndex(null)
              }
            }
          }}
          onTouchStart={(e) => {
            if (e.touches.length === 1) {
              // 检查点击目标是否是链接icon，如果是则跳过处理
              const target = e.target as HTMLElement
              if (target.closest('[data-share-link-icon]')) {
                return
              }
              // 阻止事件冒泡到 viewport，由元素自己处理
              e.stopPropagation()
              
              // 如果允许交互且未锁定，让handleItemInteraction处理
              if (allowItemInteraction && !isLocked) {
                handleItemInteraction(e, index, false)
                return
              }
              
              // 未锁定但未启用交互模式：文件元素特殊处理 - 支持层级提升和缩放
              if (!isLocked) {
                e.preventDefault()
                
                if (!project || !project.items) return
                
                const item = project.items[index] as ShareItem
                if (!item) return
                
                // 计算最大 zIndex
                const maxZIndex = Math.max(...project.items.map((it: any, idx: number) => it.zIndex ?? idx), 0)
                
                // 将文件元素移动到数组末尾（提升到最上层）
                const updatedItems = [...project.items] as ShareItem[]
                const [movedItem] = updatedItems.splice(index, 1)
                // 更新 zIndex 为最大值 + 1
                movedItem.zIndex = maxZIndex + 1
                updatedItems.push(movedItem)
                const newItemIndex = updatedItems.length - 1
                
                setProject({
                  ...project,
                  items: updatedItems as any
                })
                
                // 激活元素（使用新的索引）
                setActiveItemIndex(newItemIndex)
                setHoveredItemIndex(null)
              }
            }
          }}
          onMouseEnter={() => {
            // 文件元素：只要未锁定就允许悬停状态
            if (!isLocked) {
              setHoveredItemIndex(index)
            }
          }}
          onMouseLeave={() => {
            setHoveredItemIndex(null)
          }}
        >
          <div className="flex items-center gap-2">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
            </svg>
            <div>
              <div className="font-medium text-gray-900">{item.fileName || item.fileId || '文件'}</div>
              <div className="text-sm text-gray-500">点击查看</div>
            </div>
          </div>
          {/* 有分享链接的文件显示链接图标 */}
          {hasShareLink && (
            <div
              data-share-link-icon="true"
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                width: 32,
                height: 32,
                backgroundColor: 'rgba(6, 182, 212, 0.9)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                animation: 'sharelink-icon-pulse 2s ease-in-out infinite',
                cursor: 'pointer',
                zIndex: 1000,
                pointerEvents: 'auto'
              }}
              title="点击跳转到链接的画布"
              onMouseDown={(e) => {
                e.stopPropagation()
              }}
              onMouseUp={(e) => {
                e.stopPropagation()
                e.preventDefault()
                if (item.shareLink) {
                  handleShareLinkClick(item.shareLink, e.currentTarget)
                }
              }}
              onTouchStart={(e) => {
                e.stopPropagation()
              }}
              onTouchEnd={(e) => {
                e.stopPropagation()
                e.preventDefault()
                if (item.shareLink) {
                  handleShareLinkClick(item.shareLink, e.currentTarget)
                }
              }}
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                if (item.shareLink) {
                  handleShareLinkClick(item.shareLink, e.currentTarget)
                }
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
            </div>
          )}
          {/* 交互控制UI - 文件元素：只要未锁定且激活或悬停就显示缩放控制点 */}
          {!isLocked && (isActive || isHovered || activeItemIndex === index || hoveredItemIndex === index) && (
            <>
              {/* 缩放控制点 */}
              <div
                style={{
                  position: 'absolute',
                  right: -8,
                  top: -8,
                  width: 16,
                  height: 16,
                  backgroundColor: '#3b82f6',
                  border: '2px solid white',
                  borderRadius: '50%',
                  cursor: 'nwse-resize',
                  zIndex: 1000
                }}
                onMouseDown={(e) => {
                  controlButtonClickedRef.current = true
                  handleResizeStart(e, index)
                }}
                onTouchStart={(e) => {
                  controlButtonClickedRef.current = true
                  handleResizeStart(e, index)
                }}
              />
              {/* 旋转按钮 - 仅在启用交互模式时显示 */}
              {allowItemInteraction && (
                <div
                  style={{
                    position: 'absolute',
                    right: -8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 24,
                    height: 24,
                    backgroundColor: '#3b82f6',
                    border: '2px solid white',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation()
                    controlButtonClickedRef.current = true
                  }}
                  onClick={(e) => {
                    e.stopPropagation()
                    controlButtonClickedRef.current = true
                    handleRotateItem(index, 90)
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
                  </svg>
                </div>
              )}
            </>
          )}
        </div>
      )
    } else if (item.type === 'drawing') {
      // 画笔绘图元素渲染
      const drawingData = item.drawingData
      if (!drawingData || !drawingData.paths || drawingData.paths.length === 0) {
        return null // 没有绘图数据则不渲染
      }

      const drawingContainerStyle: React.CSSProperties = {
        ...itemStyle,
        backgroundColor: drawingData.backgroundColor === 'transparent' ? 'transparent' : (drawingData.backgroundColor || 'transparent'),
        border: 'none',
        boxShadow: 'none',
        overflow: 'visible',
        // 容器本身不拦截点击事件，让下面的元素可以被点击
        // 只有在交互模式下才允许容器响应事件
        pointerEvents: (allowItemInteraction && !isLocked) ? 'auto' : 'none'
      }

      // 渲染 SVG 路径
      const renderPaths = () => {
        return drawingData.paths.map((path: any, pathIndex: number) => {
          if (!path.points || path.points.length < 2) return null
          
          // 构建 SVG path 的 d 属性
          let pathD = `M ${path.points[0].x} ${path.points[0].y}`
          for (let i = 1; i < path.points.length; i++) {
            pathD += ` L ${path.points[i].x} ${path.points[i].y}`
          }
          
          return (
            <path
              key={path.id || pathIndex}
              d={pathD}
              stroke={path.color || '#000000'}
              strokeWidth={path.width || 2}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={path.opacity || 1}
              style={{
                ...(path.blur && path.blur > 0 ? { filter: `blur(${path.blur}px)` } : {}),
                // 只在线条描边部分响应点击事件，不阻挡下面的元素
                pointerEvents: 'stroke'
              }}
            />
          )
        })
      }

      return (
        <div
          key={item.uuid || index}
          className="canvas-item drawing-item"
          style={drawingContainerStyle}
          onMouseDown={(e) => {
            if (e.button === 0) {
              handleItemInteraction(e, index, false)
            }
          }}
          onTouchStart={(e) => {
            if (e.touches.length === 1) {
              handleItemInteraction(e, index, false)
            }
          }}
          onMouseEnter={() => {
            if (allowItemInteraction && !isLocked) {
              setHoveredItemIndex(index)
            }
          }}
          onMouseLeave={() => {
            setHoveredItemIndex(null)
          }}
        >
          <svg
            width={item.width || 400}
            height={item.height || 300}
            viewBox={`0 0 ${item.width || 400} ${item.height || 300}`}
            style={{
              width: '100%',
              height: '100%',
              display: 'block',
              backgroundColor: 'transparent',
              // SVG 本身也不拦截点击，只有路径（stroke）响应事件
              pointerEvents: 'none'
            }}
            preserveAspectRatio="xMidYMid meet"
          >
            {renderPaths()}
          </svg>
          {/* 交互控制UI - 激活或悬停时显示 */}
          {allowItemInteraction && !isLocked && (isActive || isHovered) && (
            <>
              {/* 缩放控制点 */}
              <div
                style={{
                  position: 'absolute',
                  right: -8,
                  top: -8,
                  width: 16,
                  height: 16,
                  backgroundColor: '#3b82f6',
                  border: '2px solid white',
                  borderRadius: '50%',
                  cursor: 'nwse-resize',
                  zIndex: 1000
                }}
                onMouseDown={(e) => {
                  controlButtonClickedRef.current = true
                  handleResizeStart(e, index)
                }}
                onTouchStart={(e) => {
                  controlButtonClickedRef.current = true
                  handleResizeStart(e, index)
                }}
              />
              {/* 旋转按钮 */}
              <div
                style={{
                  position: 'absolute',
                  right: -8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 24,
                  height: 24,
                  backgroundColor: '#3b82f6',
                  border: '2px solid white',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  zIndex: 1000,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseDown={(e) => {
                  e.stopPropagation()
                  controlButtonClickedRef.current = true
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  controlButtonClickedRef.current = true
                  handleRotateItem(index, 90)
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
                </svg>
              </div>
            </>
          )}
        </div>
      )
    }

    return null
  }

  // 密码输入弹窗
  if (showPasswordDialog) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6 sm:p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">需要访问密码</h2>
            <p className="text-sm text-gray-600">此分享内容已设置访问密码，请输入密码查看</p>
          </div>
          
          <div className="space-y-4">
            <div className="relative">
              <input
                type={showPasswordText ? 'text' : 'password'}
                value={passwordInput}
                onChange={(e) => {
                  setPasswordInput(e.target.value)
                  setPasswordError('')
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handlePasswordSubmit()
                  }
                }}
                placeholder="请输入访问密码"
                className={`w-full px-4 py-3 text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pr-12 ${
                  passwordError ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPasswordText(!showPasswordText)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPasswordText ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </button>
            </div>
            
            {passwordError && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                {passwordError}
              </p>
            )}
            
            <button
              onClick={handlePasswordSubmit}
              disabled={isVerifying}
              className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed font-medium"
            >
              {isVerifying ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  验证中...
                </span>
              ) : '确认'}
            </button>
          </div>
          
          <p className="mt-4 text-xs text-gray-500 text-center">
            密码验证成功后将缓存24小时，期间无需重复输入
          </p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">{error}</h2>
        </div>
      </div>
    )
  }

  if (!project) {
    // 如果还在加载中或显示加载动画，显示加载动画覆盖层
    if (isLoading || showLoadingAnimation) {
      return (
        <div className="w-full h-screen overflow-hidden bg-gray-100">
          <div className={`share-loading-screen ${loadingAnimationHidden ? 'hidden' : ''}`}>
            <div className="share-loading-text">在有限生命里，遇见那个有趣的自己。</div>
          </div>
        </div>
      )
    }
    // 加载完成后仍然没有数据，显示错误信息
    return (
      <div className="w-full h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">未找到内容</h2>
          <p className="text-gray-600">分享链接无效或已过期</p>
        </div>
      </div>
    )
  }

  // 设置画布背景色
  const canvasColors = [
    { name: '晨曦草甸', color: '#F7FAF7' },
    { name: '云端之息', color: '#F0F4F8' },
    { name: '浅米书页', color: '#FCF9F2' },
    { name: '丁香薄雾', color: '#F5F3F7' }
  ]
  const backgroundColor = project.backgroundColor || canvasColors[0].color

  return (
    <div className="w-full h-screen overflow-hidden bg-gray-100">
      {/* 光晕动画和过渡动画的CSS样式 */}
      <style>{`
        @keyframes sharelink-glow {
          0% {
            box-shadow: 0 0 20px 8px rgba(6, 182, 212, 0.4), 0 0 40px 16px rgba(6, 182, 212, 0.2), 0 0 60px 24px rgba(6, 182, 212, 0.1);
          }
          100% {
            box-shadow: 0 0 30px 12px rgba(6, 182, 212, 0.6), 0 0 50px 20px rgba(6, 182, 212, 0.35), 0 0 70px 28px rgba(6, 182, 212, 0.15);
          }
        }
        @keyframes sharelink-icon-pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 0.9;
          }
          50% {
            transform: scale(1.1);
            opacity: 1;
          }
        }
        @keyframes canvas-zoom-out {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          100% {
            transform: scale(2.5);
            opacity: 0.1;
          }
        }
        @keyframes canvas-fade-in {
          0% {
            opacity: 0.85;
          }
          100% {
            opacity: 1;
          }
        }
        .canvas-transition-wrapper {
          transform-origin: center center;
          width: 100%;
          height: 100%;
          position: absolute;
          left: 0;
          top: 0;
          pointer-events: none;
        }
        .canvas-transition-zoom-out {
          animation: canvas-zoom-out 0.7s cubic-bezier(0, 0, 0.3, 1) forwards;
        }
        .canvas-transition-zoom-in {
          animation: canvas-fade-in 0.35s ease-out forwards;
        }
      `}</style>

      {/* 过渡动画遮罩层 */}
      {isTransitioning && (
        <div 
          className="fixed inset-0 z-[9998]"
          style={{
            background: transitionPhase === 'zoomOut'
              ? 'rgba(0, 0, 0, 0.25)'
              : transitionPhase === 'zoomIn'
              ? 'rgba(0, 0, 0, 0.08)'
              : 'transparent',
            transition: 'background 0.3s ease-in-out',
            pointerEvents: 'all'
          }}
        />
      )}

      {/* 加载动画覆盖层 */}
      {showLoadingAnimation && (
        <div className={`share-loading-screen ${loadingAnimationHidden ? 'hidden' : ''}`}>
          <div className="share-loading-text">
            {(project as any)?.canvasMotto || '在有限生命里，遇见那个有趣的自己。'}
          </div>
        </div>
      )}
      
      {/* 提示信息对话框 */}
      {showMessageDialog && shareInfo?.message && (
        <div className="fixed inset-0 z-[10000] bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">提示信息</h3>
            <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6 whitespace-pre-wrap break-words">{shareInfo.message}</p>
            <div className="flex justify-end">
              <button
                onClick={() => setShowMessageDialog(false)}
                className="px-4 py-2 text-sm sm:text-base bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors w-full sm:w-auto"
              >
                我知道了
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* 分享模式：只显示画布 */}
      <div
        ref={viewportRef}
        id="canvasViewport"
        className="w-full h-full overflow-hidden relative"
        style={{ backgroundColor }}
      >
        {/* 首页链接和前进后退按钮（左上角，同一行） */}
        <div className="absolute top-2 left-2 sm:top-4 sm:left-4 z-30 flex items-center gap-2 sm:gap-3">
          {/* 首页链接 */}
          <button
            onClick={() => navigate('/')}
            onTouchStart={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
            onTouchEnd={(e) => {
              e.preventDefault()
              e.stopPropagation()
              navigate('/')
            }}
            className="flex items-center gap-2 touch-manipulation cursor-pointer"
            style={{ 
              pointerEvents: 'auto',
              WebkitTapHighlightColor: 'transparent'
            }}
            title="返回首页"
          >
            <img
              src="/logo.png"
              alt="Infinite Note"
              className="w-5 h-5 sm:w-6 sm:h-6 object-contain flex-shrink-0"
            />
            <span className="text-xs sm:text-sm font-semibold text-gray-900 whitespace-nowrap">Infinite Note</span>
          </button>

          {/* 前进后退按钮 */}
          <div className="flex items-center gap-1 sm:gap-1.5">
            {/* 后退按钮 */}
            <button
              onClick={handleGoBack}
              disabled={!canGoBack}
              onTouchStart={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              onTouchEnd={(e) => {
                e.preventDefault()
                e.stopPropagation()
                if (canGoBack) handleGoBack()
              }}
              className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center touch-manipulation transition-all ${
                canGoBack 
                  ? 'bg-white/90 hover:bg-white shadow-sm cursor-pointer' 
                  : 'bg-gray-200/50 cursor-not-allowed'
              }`}
              style={{ 
                pointerEvents: 'auto',
                WebkitTapHighlightColor: 'transparent'
              }}
              title="后退"
            >
              <svg 
                width="12" 
                height="12" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`sm:w-[14px] sm:h-[14px] ${canGoBack ? 'text-gray-700' : 'text-gray-400'}`}
              >
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>

            {/* 前进按钮 */}
            <button
              onClick={handleGoForward}
              disabled={!canGoForward}
              onTouchStart={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              onTouchEnd={(e) => {
                e.preventDefault()
                e.stopPropagation()
                if (canGoForward) handleGoForward()
              }}
              className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center touch-manipulation transition-all ${
                canGoForward 
                  ? 'bg-white/90 hover:bg-white shadow-sm cursor-pointer' 
                  : 'bg-gray-200/50 cursor-not-allowed'
              }`}
              style={{ 
                pointerEvents: 'auto',
                WebkitTapHighlightColor: 'transparent'
              }}
              title="前进"
            >
              <svg 
                width="12" 
                height="12" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`sm:w-[14px] sm:h-[14px] ${canGoForward ? 'text-gray-700' : 'text-gray-400'}`}
              >
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          </div>
        </div>

        {/* 项目名称（顶部正中间，无外框） */}
        <div 
          className="absolute top-2 sm:top-4 left-1/2 -translate-x-1/2 z-20 text-xs sm:text-sm text-gray-700 pointer-events-none max-w-[calc(100%-120px)] sm:max-w-none truncate"
          style={{
            fontFamily: "'STZhongsong', '华文中宋', 'SimSun', serif"
          }}
        >
          {project.name || '未命名画布'}
        </div>

        {/* 座右铭（左上角，固定在视口，在项目名称下方，靠左显示） */}
        {(project as any).canvasMotto && (
          <div 
            className={`absolute z-30 pointer-events-none ${project.name ? 'top-12 sm:top-16' : 'top-12 sm:top-16'} left-4`}
            style={{
              maxWidth: 'calc(100vw - 120px)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            <div
              className="text-gray-800 text-xs sm:text-sm"
              style={{
                fontFamily: "'STZhongsong', '华文中宋', 'SimSun', serif",
                color: '#374151',
                lineHeight: '1.6',
                opacity: isMottoAnimating ? 0.8 : 1,
                transition: 'opacity 0.3s ease',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {mottoDisplayText}
              {isMottoAnimating && (
                <span 
                  className="inline-block w-0.5 h-5 bg-gray-800 ml-1"
                  style={{
                    animation: 'blink 1s infinite',
                    verticalAlign: 'middle',
                    marginLeft: '2px'
                  }}
                />
              )}
            </div>
          </div>
        )}

        {/* 操作提示（右上角） */}
        <div className="absolute top-2 right-2 sm:top-4 sm:right-4 z-20 flex items-center gap-2">
          <div className="px-2 py-1 sm:px-3 sm:py-1.5 bg-blue-50 border border-blue-200 rounded-md text-[10px] sm:text-xs text-blue-600 pointer-events-none shadow-sm">
            <div className="flex items-center gap-1 sm:gap-2">
              <svg width="12" height="12" className="sm:w-[14px] sm:h-[14px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M12 16v-4"></path>
                <path d="M12 8h.01"></path>
              </svg>
              <span className="hidden sm:inline">拖动平移 | 滚轮缩放</span>
              <span className="sm:hidden">拖动平移 | 双指缩放</span>
            </div>
          </div>
          
          {/* 如果 lockInterface 为 false（不锁定），显示锁定icon和恢复icon */}
          {/* 如果 lockInterface 为 true 或 undefined（锁定），不显示任何按钮 */}
          {shareInfo && shareInfo.lockInterface === false && (
            <>
              {/* 锁定按钮 */}
              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleToggleLock()
                }}
                onTouchStart={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
                onTouchEnd={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleToggleLock()
                }}
                className="px-3 py-1.5 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 active:bg-gray-100 transition-all cursor-pointer flex items-center gap-1.5 touch-manipulation"
                style={{ pointerEvents: 'auto', zIndex: 21, WebkitTapHighlightColor: 'transparent' }}
                title={isLocked ? '解锁界面' : '锁定界面'}
              >
                {isLocked ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 9 0"></path>
                  </svg>
                )}
                <span className="text-xs font-medium text-gray-700 hidden sm:inline">{isLocked ? '解锁' : '锁定'}</span>
              </button>
              
              {/* 恢复按钮 */}
              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleResetItems()
                }}
                className="px-3 py-1.5 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 active:bg-gray-100 transition-all cursor-pointer flex items-center gap-1.5 touch-manipulation"
                style={{ pointerEvents: 'auto', zIndex: 21, WebkitTapHighlightColor: 'transparent' }}
                title="恢复原样"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path>
                  <path d="M21 3v5h-5"></path>
                  <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path>
                  <path d="M3 21v-5h5"></path>
                </svg>
                <span className="text-xs font-medium text-gray-700 hidden sm:inline">恢复</span>
              </button>
            </>
          )}
        </div>

        {/* 缩放控制滑块（右侧） */}
        <div 
          ref={zoomSliderRef}
          className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-30 flex flex-col items-center gap-2"
          style={{ pointerEvents: 'auto' }}
        >
          {/* 滑块轨道 */}
          <div 
            className="w-1 bg-gray-300 rounded-full relative touch-none"
            style={{ height: '200px', minHeight: '150px' }}
            onMouseDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
              handleZoomSliderClick(e)
            }}
            onTouchStart={(e) => {
              e.stopPropagation()
              handleZoomSliderTouch(e)
            }}
          >
            {/* 滑块圆点 */}
            <div
              className="absolute left-1/2 -translate-x-1/2 w-6 h-6 bg-blue-500 rounded-full shadow-lg cursor-grab active:cursor-grabbing touch-none"
              style={{
                top: `${(1 - (canvasScale - 0.1) / (5 - 0.1)) * 100}%`,
                transform: 'translate(-50%, -50%)',
                transition: isZoomDragging ? 'none' : 'top 0.1s ease-out'
              }}
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setIsZoomDragging(true)
                handleZoomSliderDrag(e)
              }}
              onTouchStart={(e) => {
                e.stopPropagation()
                setIsZoomDragging(true)
                handleZoomSliderTouchDrag(e)
              }}
            >
              <div className="w-full h-full rounded-full bg-white border-2 border-blue-500"></div>
            </div>
          </div>
          
          {/* 缩放标签 */}
          <div className="text-xs text-gray-600 font-medium bg-white px-2 py-1 rounded shadow-sm">
            {Math.round(canvasScale * 100)}%
          </div>
        </div>

        {/* 定位到所有元素中心按钮（底部居中，但不要太靠下） */}
        {project && project.items && project.items.length > 0 && (
          <div className="absolute bottom-16 sm:bottom-20 left-1/2 -translate-x-1/2 z-20 flex items-center justify-center">
            <button
              onClick={(e) => {
                // PC端点击事件
                e.preventDefault()
                e.stopPropagation()
                handleCenterViewOnAllItems(e)
              }}
              onTouchEnd={(e) => {
                // 移动端触摸结束事件 - 专门处理移动端点击
                e.preventDefault()
                e.stopPropagation()
                handleCenterViewOnAllItems(e)
              }}
              className="px-4 py-2.5 bg-white border border-gray-300 rounded-lg shadow-lg hover:bg-gray-50 active:bg-gray-100 transition-all touch-manipulation cursor-pointer flex items-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)',
                borderColor: '#a855f7',
                color: '#6b21a8',
                pointerEvents: 'auto',
                zIndex: 21,
                WebkitTapHighlightColor: 'transparent'
              }}
              title="定位到所有元素中心"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <circle cx="12" cy="12" r="3"></circle>
                <line x1="12" y1="2" x2="12" y2="6"></line>
                <line x1="12" y1="18" x2="12" y2="22"></line>
                <line x1="2" y1="12" x2="6" y2="12"></line>
                <line x1="18" y1="12" x2="22" y2="12"></line>
              </svg>
              <span className="text-sm font-medium">定位到中心</span>
            </button>
          </div>
        )}

        {/* 画布内容 */}
        <div 
          className={`canvas-transition-wrapper ${
            transitionPhase === 'zoomOut' ? 'canvas-transition-zoom-out' : 
            transitionPhase === 'zoomIn' ? 'canvas-transition-zoom-in' : ''
          }`}
        >
          <div
            ref={contentRef}
            id="canvasContent"
            className="absolute left-0 top-0 w-full h-full pointer-events-none overflow-visible"
            style={{
              '--offset-x': `${canvasOffset.x}px`,
              '--offset-y': `${canvasOffset.y}px`,
              '--scale': canvasScale,
              // transform 始终设置，opacity 由 CSS 动画控制
              transform: `translate(${canvasOffset.x}px, ${canvasOffset.y}px) scale(${canvasScale})`,
              transformOrigin: '0 0'
            } as React.CSSProperties}
          >
            {/* 渲染所有项目 */}
            {project.items && project.items.length > 0 ? (
              project.items.map((item, index) => renderCanvasItem(item, index))
            ) : (
              <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 text-gray-400 text-center">
                <p className="text-lg">画布为空</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 设置对话框 */}
      {showSettingsDialog && (
        <div className="fixed inset-0 z-[10000] bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">分享页面设置</h3>
            
            <div className="space-y-4">
              {/* 允许操作元素开关 */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label className="text-sm font-medium text-gray-700">允许操作元素</label>
                  <p className="text-xs text-gray-500 mt-1">启用后可以拖动、缩放、旋转元素（不会保存）</p>
                </div>
                <button
                  onClick={() => {
                    if (isLocked) return  // 如果锁定，不允许切换
                    setAllowItemInteraction(!allowItemInteraction)
                    if (!allowItemInteraction) {
                      // 启用时，点击任意元素激活它
                      setActiveItemIndex(null)
                    } else {
                      // 禁用时，清除激活状态
                      setActiveItemIndex(null)
                      setInteractionMode('none')
                    }
                  }}
                  disabled={isLocked}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    allowItemInteraction && !isLocked ? 'bg-blue-600' : 'bg-gray-300'
                  } ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      allowItemInteraction && !isLocked ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowSettingsDialog(false)}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                确定
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 图片右键菜单 */}
      {contextMenuState.visible && (
        <ImageContextMenu
          x={contextMenuState.x}
          y={contextMenuState.y}
          itemIndex={contextMenuState.itemIndex}
          onClose={() => setContextMenuState({ visible: false, itemIndex: -1, x: 0, y: 0 })}
          onView={() => {
            const item = project?.items?.[contextMenuState.itemIndex]
            if (item && project?.items) {
              // 只对有弹窗的元素类型执行查看操作
              if (item.type !== 'image' && item.type !== 'text' && item.type !== 'file') {
                return // drawing、connector、video、audio 等类型没有弹窗，不执行查看
              }
              // 找到viewableIndex
              const viewableItems = project.items
                .map((it, idx) => ({ it, idx }))
                .filter(({ it }) => {
                  // 排除欢迎文本
                  if (it.type === 'text' && (it as ShareItem).isWelcomeText) {
                    return false
                  }
                  // 只包含有弹窗的元素类型
                  return it.type === 'image' || it.type === 'text' || it.type === 'file'
                })
                .map(({ idx }) => idx)
              
              const viewableIndex = viewableItems.indexOf(contextMenuState.itemIndex)
              if (viewableIndex !== -1) {
                setViewerState({
                  type: item.type,
                  currentIndex: viewableIndex
                })
              }
            }
          }}
          onDownload={() => {
            const item = project?.items?.[contextMenuState.itemIndex]
            if (item && item.type === 'image') {
              const shareItem = item as ShareItem
              const fileId = shareItem.fileId || shareItem.imageId
              if (fileId && shareInfo?.userUuid && project?.uuid) {
                 const url = getFileUrl(fileId, shareInfo.userUuid, project.uuid)
                 const a = document.createElement('a')
                 a.href = url
                 a.download = shareItem.fileName || `image-${fileId}.png`
                 document.body.appendChild(a)
                 a.click()
                 document.body.removeChild(a)
               }
            }
          }}
          onSendToBack={() => {
             // 分享预览模式不支持修改
             alert('预览模式下无法修改')
          }}
          onSendToFront={() => {
             // 分享预览模式不支持修改
             alert('预览模式下无法修改')
          }}
          onRotate={() => {
             // 分享预览模式不支持修改
             alert('预览模式下无法修改')
          }}
          onLink={() => {
             if (confirm('要在画布上添加链接或进行编辑，需要先将此项目添加到您的库中。\n是否立即获取此项目？')) {
                 navigate('/marketplace')
             }
          }}
        />
      )}
      {viewerState.type && (() => {
        const currentItem = getCurrentViewItem()
        if (!currentItem) return null

        // 只处理有弹窗的元素类型
        if (viewerState.type !== 'image' && viewerState.type !== 'text' && viewerState.type !== 'file') {
          // 如果类型不支持弹窗，关闭弹窗
          handleCloseViewer()
          return null
        }

        const currentIndex = viewerState.currentIndex
        const totalCount = viewableItems.length
        const hasPrev = currentIndex > 0
        const hasNext = currentIndex < totalCount - 1

        if (viewerState.type === 'image') {
          // 构建图片URL - 支持 OSS 和本地存储
          // 统一使用fileId，兼容旧数据的imageId
          const fileId = currentItem.fileId || currentItem.imageId
          const hasExtension = fileId && fileId.includes('.')
          
          // 常见的图片扩展名列表（按常见程度排序）
          const imageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'webp']
          
          // 获取当前尝试的扩展名索引
          const getCurrentExtensionIndex = () => {
            if (hasExtension || !fileId) return -1
            if (!imageExtensionIndexMap.current.has(fileId)) {
              imageExtensionIndexMap.current.set(fileId, 0)
            }
            return imageExtensionIndexMap.current.get(fileId)!
          }
          
          const currentExtensionIndex = getCurrentExtensionIndex()
          
          // 查看器使用原图（fileId），不使用压缩图（thumbnailFileId）
          // 如果 fileId 没有扩展名，需要尝试多种扩展名（兼容旧数据）
          let imageUrl = ''
          const originalFileId = currentItem.fileId || currentItem.imageId // 查看器始终使用原图
          const originalHasExtension = originalFileId && originalFileId.includes('.')
          
          if (originalFileId && shareInfo?.userUuid && project?.uuid) {
            if (originalHasExtension) {
              // 已经有扩展名，直接使用
              imageUrl = getFileUrl(originalFileId, shareInfo.userUuid, project.uuid)
            } else {
              // 没有扩展名，使用当前尝试的扩展名
              const extension = imageExtensions[currentExtensionIndex] || 'png'
              imageUrl = getFileUrl(originalFileId, shareInfo.userUuid, project.uuid, extension)
            }
          } else if (currentItem.imageSrc) {
            imageUrl = currentItem.imageSrc
          }

          if (!imageUrl) return null

          return (
            <ImageViewerDialog
              key={`${originalFileId}-${currentExtensionIndex}-${imageUrlKey}`} // 添加key以强制重新渲染
              imageUrl={imageUrl}
              fileName={originalFileId ? (originalHasExtension ? `image-${originalFileId}` : `image-${originalFileId}.${imageExtensions[currentExtensionIndex] || 'png'}`) : 'image.png'}
              onError={() => {
                // 如果 fileId 没有扩展名，尝试其他扩展名
                const originalFileId = currentItem.fileId || currentItem.imageId
                if (!originalHasExtension && originalFileId && shareInfo?.userUuid && project?.uuid) {
                  const nextIndex = currentExtensionIndex + 1
                  
                  // 如果还有未尝试的扩展名，尝试下一个
                  if (nextIndex < imageExtensions.length) {
                    imageExtensionIndexMap.current.set(originalFileId, nextIndex)
                    setImageUrlKey(prev => prev + 1) // 触发重新渲染
                  }
                }
              }}
              onClose={handleCloseViewer}
              onPrev={hasPrev ? handlePrevViewer : undefined}
              onNext={hasNext ? handleNextViewer : undefined}
              hasPrev={hasPrev}
              hasNext={hasNext}
              currentIndex={currentIndex}
              totalCount={totalCount}
              allowDownload={shareInfo?.allowDownload !== false}
              shareLink={currentItem.shareLink || ''}
              onShareLinkChange={async (link: string) => {
                // 保存链接到访问者自己的项目中
                await handleSaveShareLink(currentItem, link)
              }}
              onOpenLinkSelector={() => {
                // 检查访问者是否有UUID
                if (!visitorUserUuid) {
                  alert('本地没有用户UUID，请先访问主页面创建用户。')
                  return
                }
                // 打开链接选择对话框
                setLinkSelectorCurrentLink(currentItem.shareLink || '')
                // 保存当前item的引用，用于后续保存
                setLinkSelectorItemIndex(viewableItems[viewerState.currentIndex])
                setShowLinkSelectorDialog(true)
              }}
              showShareLinkInput={true} // 显示链接设置功能
              shareLinkNotLoggedInMessage={visitorUserUuid ? undefined : '本地没有用户UUID，请先访问主页面创建用户。'}
            />
          )
        } else if (viewerState.type === 'text') {
          return (
            <TextViewerDialog
              content={currentItem.content || ''}
              fileName={`text-${currentItem.uuid || currentIndex}.txt`}
              fontSize={currentItem.fontSize || settings.fontSize || 14}
              fontFamily={currentItem.fontFamily || settings.fontFamily || 'Microsoft YaHei, 微软雅黑, Arial, sans-serif'}
              onClose={handleCloseViewer}
              onPrev={hasPrev ? handlePrevViewer : undefined}
              onNext={hasNext ? handleNextViewer : undefined}
              hasPrev={hasPrev}
              hasNext={hasNext}
              currentIndex={currentIndex}
              totalCount={totalCount}
              allowDownload={shareInfo?.allowDownload !== false}
              shareLink={currentItem.shareLink || ''}
              onShareLinkChange={async (link: string) => {
                // 保存链接到访问者自己的项目中
                await handleSaveShareLink(currentItem, link)
              }}
              onOpenLinkSelector={() => {
                // 检查访问者是否有UUID
                if (!visitorUserUuid) {
                  alert('本地没有用户UUID，请先访问主页面创建用户。')
                  return
                }
                // 打开链接选择对话框
                setLinkSelectorCurrentLink(currentItem.shareLink || '')
                // 保存当前item的引用，用于后续保存
                setLinkSelectorItemIndex(viewableItems[viewerState.currentIndex])
                setShowLinkSelectorDialog(true)
              }}
              showShareLinkInput={true}
              shareLinkNotLoggedInMessage={visitorUserUuid ? undefined : '本地没有用户UUID，请先访问主页面创建用户。'}
            />
          )
        } else if (viewerState.type === 'file') {
          // 构建文件URL - 支持 OSS 和本地存储
          const fileUrl = currentItem.fileId && shareInfo?.userUuid && project?.uuid
            ? getFileUrl(currentItem.fileId, shareInfo.userUuid, project.uuid)
            : ''

          if (!fileUrl) return null

          return (
            <FileViewerDialog
              fileUrl={fileUrl}
              fileName={currentItem.fileName || currentItem.fileId || 'file'}
              fileType={undefined} // 可以从 item 中获取 fileType
              localFilePath={null} // 分享页面不支持本地文件路径
              onClose={handleCloseViewer}
              onPrev={hasPrev ? handlePrevViewer : undefined}
              onNext={hasNext ? handleNextViewer : undefined}
              hasPrev={hasPrev}
              hasNext={hasNext}
              currentIndex={currentIndex}
              totalCount={totalCount}
              allowDownload={shareInfo?.allowDownload !== false}
              shareLink={currentItem.shareLink || ''}
              onShareLinkChange={async (link: string) => {
                // 保存链接到访问者自己的项目中
                await handleSaveShareLink(currentItem, link)
              }}
              onOpenLinkSelector={() => {
                // 检查访问者是否有UUID
                if (!visitorUserUuid) {
                  alert('本地没有用户UUID，请先访问主页面创建用户。')
                  return
                }
                // 打开链接选择对话框
                setLinkSelectorCurrentLink(currentItem.shareLink || '')
                // 保存当前item的引用，用于后续保存
                setLinkSelectorItemIndex(viewableItems[viewerState.currentIndex])
                setShowLinkSelectorDialog(true)
              }}
              showShareLinkInput={true}
              shareLinkNotLoggedInMessage={visitorUserUuid ? undefined : '本地没有用户UUID，请先访问主页面创建用户。'}
            />
          )
        }

        return null
      })()}

      {/* 链接选择对话框 */}
      {showLinkSelectorDialog && (
        <ShareLinkSelectorDialog
          onClose={() => setShowLinkSelectorDialog(false)}
          onSelect={async (shareUrl: string) => {
            // 获取当前查看的item
            const currentViewItem = getCurrentViewItem()
            if (currentViewItem) {
              await handleSaveShareLink(currentViewItem, shareUrl)
            }
          }}
          currentLink={linkSelectorCurrentLink}
        />
      )}
      
      {/* 临时提示（不阻塞，自动消失） */}
      {tempTip && (
        <div 
          className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 
                     bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg
                     animate-fade-in-up"
          style={{
            animation: 'fadeInUp 0.3s ease-out'
          }}
        >
          {tempTip}
        </div>
      )}
      
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translate(-50%, 10px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
      `}</style>
    </div>
  )
}

export default ShareView
